import React, { Fragment, useState, useEffect, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Checkbox,
  tableCellClasses,
  InputLabel,
  FormControl,
  TableCell,
  Paper,
  Select,
  MenuItem,
  ListItemText,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import SelectField from "components/Select";
import { PrimaryButton } from "components/Buttons";
import Colors from "assets/Style/Colors";
import { CheckIcon, FontFamily, PendingIcon } from "assets";
import { makeStyles } from "@mui/styles";
import styled from "@emotion/styled";
import { useForm, Controller } from "react-hook-form";
import { ErrorToaster } from "components/Toaster";
import moment from "moment";
import ClientServices from "services/Client";
import SystemServices from "services/System";
import { useAuth } from "context/UseContext";
import FinanceServices from "services/Finance";
import { useNavigate } from "react-router-dom";
import CurrencyServices from "services/Currency";
import { useSelector } from "react-redux";
import { CircleLoading } from "components/Loaders";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { CommaSeparator, handleExportWithComponent } from "utils";
import { PDFExport } from "@progress/kendo-react-pdf";
import DatePicker from "components/DatePicker";
import ShippingSoaPdf from "components/Pdf/ShippingSoaPdf";
import ShippingLandscapeLogo from "components/Pdf/shipping_short_landscape.png";

const toNumber = (value) => {
  const normalized = String(value ?? "").replace(/,/g, "").trim();
  const parsedValue = Number(normalized);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getShippingChargesTotal = (item) => {
  const vehicleValue =
    toNumber(item?.booking?.value) + toNumber(item?.booking?.other_charges);
  const includeShippingCharges2 = vehicleValue >= 50000;

  return (
    toNumber(item?.shipping_charges) +
    (includeShippingCharges2 ? toNumber(item?.shipping_charges_2) : 0)
  );
};

const getSafeSoaPdfFileName = (customerName) => {
  const safeCustomer = String(customerName ?? "export")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .slice(0, 120);

  return `Shipping-SOA-${safeCustomer || "export"}.pdf`;
};

// *For Table Style
const Row = styled(TableRow)(({ theme }) => ({
  border: 0,
}));

const Cell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    border: 0,
    padding: "15px 5px",
    textAlign: "center",
    // whiteSpace: "nowrap",
    background: Colors.primary,
    color: Colors.white,
    writingMode: "vertical-rl",
    textOrientation: "mixed",
    transform: "rotate(180deg)",
    verticalAlign: "middle",
    height: "125px",
    lineHeight: "1.2",
    letterSpacing: "2px",
    whiteSpace: "normal", // ✅ allow wrapping
    wordBreak: "break-word", // ✅ allow break mid-word
    overflowWrap: "break-word",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: FontFamily.NunitoRegular,
    textAlign: "center",
    whiteSpace: "nowrap",
    padding: "5px !important",

    ".MuiBox-root": {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      justifyContent: "center",
      ".MuiBox-root": {
        cursor: "pointer",
      },
    },
    svg: {
      width: "auto",
      height: "24px",
    },
    ".MuiTypography-root": {
      textTransform: "capitalize",
      fontFamily: FontFamily.NunitoRegular,
      whiteSpace: "nowrap",
    },
    ".MuiButtonBase-root": {
      width: "28px",
      height: "28px",
    },
  },
}));

const useStyles = makeStyles({
  loaderWrap: {
    display: "flex",
    height: 100,
    "& svg": {
      width: "40px !important",
      height: "40px !important",
    },
  },
});

function ShippingSOAV2(props) {
  const printMode = !!(props && props.printMode);
  const printCustomerId = props && props.printCustomerId;
  const printFromDate = props && props.printFromDate;
  const printToDate = props && props.printToDate;

  const { user } = useAuth();
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const { usdExchangeRate, cadExchangeRate } = useSelector(
    (state) => state.navigationReducer,
  );

  // *For Total Amount
  const [totalAmount, setTotalAmount] = useState([]);

  const [allSums, setAllSums] = useState();

  // *For Client Dropdown
  const [clients, setClients] = useState([]);

  const [loader, setLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  // Download-in-progress flag for the Excel export — same purpose as the
  // existing `isGeneratingPdf` flag for the PDF export. Keeps users from
  // firing a second export (or a Search) while the current one is still
  // building.
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  // The customer id whose data is currently sitting in `shippingSOA` /
  // related state. Set only when a fetch succeeds; cleared as soon as a new
  // fetch starts. This is the fix for the "downloaded PDF has one customer's
  // header but another customer's rows" bug — Download PDF / Excel are only
  // enabled when `loadedCustomerId === selectedClient?.id` (i.e. what's on
  // screen actually belongs to the customer the user thinks it does).
  const [loadedCustomerId, setLoadedCustomerId] = useState(null);

  // *for Payment Type

  const [paymentType, setPaymentType] = useState("aed");

  const [totalVCC, settotalVCC] = useState();
  const [chartReady, setChartReady] = React.useState(false);

  // *For Paid Status
  const [selectedStatus, setSelectedStatus] = useState({
    id: "all",
    name: "All",
  });
  const [selectedGPStatus, setSelectedGPStatus] = useState({
    id: "all",
    name: "All",
  });

  // *For Destination filter
  const [destinations, setDestinations] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);

  // *For Cashier Account
  const [cashierAccounts, setCashierAccounts] = useState([]);
  const [selectedCashierAccount, setSelectedCashierAccount] = useState(null);

  // *For Pagination
  const [totalCount, setTotalCount] = useState(0);
  const [pageLimit, setPageLimit] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [shippingSOA, setShippingSOA] = useState();

  const [shippingVault, setShippingVault] = useState();
  const [customDue, setCustomDue] = useState();
  const [vehicleVault, setVehicleVault] = useState();
  const [parkingDue, setParkingDue] = useState();

  // *For Countries
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // *Cashier Details
  const [cashierDetails, setCashierDetails] = useState(false);

  // *For Vin and Lot
  const [vin, setVin] = useState();
  const [selectedVin, setSelectedVin] = useState();
  const [lot, setLot] = useState();
  const [selectedLot, setSelectedLot] = useState();

  // *For Filters
  const [filters, setFilters] = useState({});

  const [showTotal, setShowTotal] = useState(false);

  const [walletBalance, setWalletBalance] = useState();

  const [totalPayment, setTotalPayment] = useState();

  // *wallet Id
  const [walletId, setWalletId] = useState();

  // *For Currencies
  const [currencyExchangeRate, setCurrencyExchangeRate] = useState();

  // *for button Disable
  const [buttonState, setButtonState] = useState(false);

  const [showTable, setShowTable] = useState(false);
  const [totalBalance, setTotalBalance] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [mainTotal, setMainTotal] = useState(0);
  const [subTotal, setSubTotal] = useState(0);

  // first
  const [loss, setLoss] = useState(0);
  const [items, setItems] = useState([]);
  const [balances, setBalances] = useState({});
  // *for details
  const [sendDetails, setSendDetails] = useState();
  // Handle Date Filter
  // In print mode, the cron passes from_date/to_date in the URL so the
  // generated PDF covers the full SOA period. Default fallback: 2025-01-01
  // (matches the backend payload builder) → today.
  const [fromDate, setFromDate] = useState(
    printMode
      ? printFromDate
        ? moment(printFromDate).startOf("day").toDate()
        : moment("2025-01-01").startOf("day").toDate()
      : moment().subtract(3, "months").startOf("day").toDate(),
  );
  const [toDate, setToDate] = useState(
    printMode
      ? printToDate
        ? moment(printToDate).endOf("day").toDate()
        : moment().endOf("day").toDate()
      : moment().endOf("day").toDate(),
  );

  // useEffect(() => {
  //   filterData();
  // }, [fromDate, toDate, shippingSOA]);

  const filterData = () => {
    if (!shippingSOA || !Array.isArray(shippingSOA)) {
      console.error("shippingSOA is not properly initialized.");
      return;
    }

    let filteredData = shippingSOA;

    if (fromDate && toDate) {
      const formattedFromDate = moment(fromDate).format("YYYY-MM-DD");
      const formattedToDate = moment(toDate).format("YYYY-MM-DD");

      filteredData = filteredData.filter((item) => {
        const purchaseDate = moment(item?.booking?.purchase_date).format(
          "YYYY-MM-DD",
        );
        return (
          purchaseDate >= formattedFromDate && purchaseDate <= formattedToDate
        );
      });
      setFilteredData(filteredData);
    }
    if (!fromDate || !toDate) {
      setFilteredData([]);
    }
  };

  const handleFromDate = (date) => {
    setFromDate(date);
  };

  const handleToDate = (date) => {
    setToDate(date);
  };
  // *For Tooltip
  const [copied, setCopied] = useState(false);

  const copyContent = (text) => {
    const contentToCopy = text;
    navigator.clipboard.writeText(contentToCopy);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 300);
  };

  const classes = useStyles();

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ mode: "all" });

  const {
    register: register2,
    handleSubmit: handleSubmit2,
    setValue,
    getValues: getValues2,
    formState: { errors: errors2 },
    reset,
  } = useForm();
  // *For Shipping SOA
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  // const handlePdfExport = () => {
  //   setIsGeneratingPdf(true);
  //   setTimeout(() => {
  //     handleExportWithComponent(contentRef);
  //     setIsGeneratingPdf(false);
  //   }, 0);
  // };
  const tableHead = [
    "Sr. No",
    "Buy Date",
    "Model/ Make/ Color",
    "LOT",
    "VIN",
    "Destination",
    "Arrived Date",
    "Ship Charges",
    "Auction Late Pmt Fee",
    "Ship. Late Pmt Fee",
    "Storage",
    "Terminal Storages",
    "Category A",
    "Broker Fee",
    "Title Fee",
    "Attest. Fee",
    "SUV Fee",
    "Hybrid/ Electric",
    "Heavy Equip. Fee",
    "Parts Pickup Fee",
    "Wrapping Fee",
    "Relist Reversal",
    "WR. Fee",
    "Fuel Surcharge",
    "Color Change",
    "THC Charges",
    "Intl Clearance",
    "Inland Transportation",
    "INTNL Export Charges",
    "Emergency Dep",
    "Custom",
    "VAT",
    "Sub Total",
    "Discount",
    "War Sur.Char Disc",
    "Net Due",
    "Paid",
    "Balance Due",
    "Past Due Days",
    "Shipping Status",
    // "Vcc",
    // "Status",
    // "Other Charge",
    // "Total",
    // "Action",
  ];
  const [filteredData, setFilteredData] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [pdfClient, setPdfClient] = useState(null);
  const [totalShippingWallet, setTotalShippingWallet] = useState(0);
  const [totalVehicleWallet, setTotalVehicleWallet] = useState(0);
  const [shippingReceivableCV, setShippingReceivableCV] = useState(0);
  const [totalCustomWallet, setTotalCustomWallet] = useState(0);
  const [totalParkingWallet, setTotalParkingWallet] = useState(0);

  useEffect(() => {
    if (selectedClient) {
      setPdfClient(selectedClient);
    }
  }, [selectedClient]);
  const pdfTableHead = tableHead.filter((item) => item !== "Action");
  // const handleExportWithComponent = () => {
  //   const htmlContent = CreatePdf(
  //     pdfTableHead,
  //     filteredData,
  //     "Shipping Statement Of Account",
  //     currencyExchangeRate,
  //     mainTotal,
  //     totalBalance,
  //     totalPaid,
  //     shippingVault,
  //     totalVCC,
  //     vehicleVault,
  //     allSums,
  //     dueBreakup,
  //     "",
  //     "",
  //     "",
  //     fromDate,
  //     toDate,
  //     selectedStatus,
  //     pdfClient,
  //   );
  //   downloadPDF(htmlContent, "Shipping Soa", filteredData);
  // };
  const [visibleColumns, setVisibleColumns] = useState([
    ...Array(tableHead?.length).keys(),
  ]);
  const [pdfVisibleColumns, setPdfVisibleColumns] = useState(visibleColumns);
  let NoOfVCC = 0;

  // *For Clients
  const getClientDropdown = async (nationalityId) => {
    try {
      const { data } = await ClientServices.getClientDropdown();
      setClients(data?.customers?.rows);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  // *For Get Currencies
  const getCurrencies = async (currency) => {
    try {
      let params = {
        detailed: true,
      };
      const { data } = await CurrencyServices.getCurrencies(params);

      // Look up the USD rate by currency code (not a hardcoded array index) —
      // matches the app-wide logic in layouts/Dashboard. This is the AED->USD
      // divisor used throughout this page (e.g. wallet/parking conversions).
      setCurrencyExchangeRate(
        parseFloat(
          data?.currencies?.find((e) => e?.currency === "usd")?.conversion_rate,
        ),
      );
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const toDateOnly = (d) => (d ? moment(d).format("YYYY-MM-DD") : undefined);

  // *For Client Costing
  const getClientCosting = async (page, limit, filter) => {
    setLoader(true);
    // Clear the "data belongs to customer X" marker at the start of every
    // fetch so the download buttons stay disabled until this fetch either
    // finishes cleanly or the user searches for a different customer. This
    // prevents someone from downloading while a stale customer's rows are
    // still on screen mid-fetch.
    setLoadedCustomerId(null);
    try {
      const Page = page ? page : currentPage;
      // const Limit = limit ? limit : pageLimit;
      const Filter = { ...filters, ...filter };
      setCurrentPage(Page);
      // setPageLimit(Limit);
      setFilters(Filter);
      let params = {
        page: Page,
        // limit: Limit,
        invoice: true,
      };
      params = { ...params, ...Filter };
      // Capture the customer id this fetch is FOR up front so we can pin the
      // "loaded customer" marker to it on success, independent of any later
      // state changes (e.g. the user switched selection mid-flight).
      const fetchedCustomerId = Filter?.customer_id ?? null;
      const { data } = await ClientServices.getClientCostingSoa(params);
      settotalVCC(0);
      let totalShippingCharges = 0;
      let totalLateFee = 0;
      let totalStorage = 0;
      let totalCategoryA = 0;
      let totalBrokerFee = 0;
      let totalTitleFee = 0;
      let totalOtherCharges = 0;
      let totalCustomDuty = 0;
      let totalVAT5Percent = 0;
      let totalReceivingAmount = 0;
      let subtotal = 0;
      let total = 0;
      let balance = 0;
      let paid = 0;
      let totalVat = 0;
      let totalCustom = 0;
      let discount = 0;
      let warSurDisc = 0;

      // Iterate through the array and accumulate the shipping charges

      data?.costings?.rows.forEach((item) => {
        const date = moment(item?.vehicle?.vcc?.vcc_expiry_date).format(
          "DD-MM-YYYY",
        );

        const targetDate = moment(date, "DD-MM-YYYY");
        let daysRemaining = targetDate > moment();
        if (item?.vehicle?.vcc) {
          NoOfVCC++;
        }
        // console.log(NoOfVCC);
        settotalVCC(NoOfVCC);
        totalShippingCharges += getShippingChargesTotal(item);
        totalLateFee += parseFloat(item?.late_fee);
        totalStorage += parseFloat(item?.storage);
        totalCategoryA += parseFloat(item?.category_a);
        totalBrokerFee += parseFloat(item?.broker_fee);
        totalTitleFee += parseFloat(item?.title_fee);
        totalOtherCharges += parseFloat(item?.other_charge);
        totalCustomDuty += parseFloat(
          item?.vehicle?.vcc?.custom_due
            ? item?.custom_duty == 0
              ? item?.vehicle?.vcc?.custom_charges_aed / currencyExchangeRate
              : item?.custom_duty
            : 0,
        );
        totalVAT5Percent += parseFloat(
          item?.vehicle?.vcc?.vat_due
            ? item?.vat == 0
              ? item?.vehicle?.vcc?.vat_charges_aed / currencyExchangeRate
              : item?.vat
            : 0,
        );
        totalReceivingAmount += parseFloat(item?.receiving_amount);
        subtotal += parseFloat(item?.subtotal);
        balance += parseFloat(item?.invoice?.balance);
        total += parseFloat(item?.invoice?.total);
        paid += parseFloat(item?.invoice?.paid);
        totalCustom += parseFloat(item?.invoice?.custom_total);
        totalVat += parseFloat(item?.invoice?.vat_total);
        discount += parseFloat(item?.discount);
        warSurDisc += parseFloat(item?.war_surcharge_disc || 0);
      });

      const shippingReceivableWithCustomVat =
        totalCustomDuty + totalVAT5Percent;
      const totalSubTotal = total + discount + warSurDisc;
      const totalNetDue = totalSubTotal - discount - warSurDisc;
      setShippingReceivableCV(shippingReceivableWithCustomVat);
      setTotalBalance(balance);
      setSubTotal(totalSubTotal);
      setMainTotal(totalNetDue);
      // setMainTotal(total)
      setTotalPaid(paid);

      setAllSums(balance);
      // let filterData;
      // if (selectedStatus?.name === 'Unpaid') {
      //   filterData =data?.costings?.rows?.filter((i) => {
      //     return (
      //       i && i?.invoice?.balance !== "0.000"
      //     )
      //   })
      // } else if (selectedStatus?.name === "Paid") {
      //   filterData = data?.costings?.rows?.filter((i) => {

      //     return (
      //       i && i?.invoice?.total === i?.invoice?.paid
      //     )
      //   })
      // } else if (selectedStatus?.name === 'All') {
      //   filterData = data?.costings?.rows
      // }

      const sortedByArrivedDateAsc = data?.costings?.rows.sort((a, b) => {
        const dateA = new Date(a?.vehicle?.arrived_port_date);
        const dateB = new Date(b?.vehicle?.arrived_port_date);
        return dateA - dateB; // Ascending
      });

      setShippingSOA(sortedByArrivedDateAsc);

      setTotalCount(data?.costings?.count);
      // Fetch completed cleanly for this customer — record which customer's
      // data is now on screen so the Download PDF / Excel buttons can unlock.
      setLoadedCustomerId(fetchedCustomerId);
    } catch (error) {
      ErrorToaster(error);
      // In print mode the toaster is invisible — record the failure so the
      // export effect can surface it as window.__SOA_ERROR__ to the cron.
      if (printMode) {
        setPrintFetchError(
          (error && (error.message || error.response?.data?.message)) ||
            String(error),
        );
      }
    } finally {
      setLoader(false);
    }
  };
  // *For Apply Filters
  const applyFilter = async () => {
    try {
      let data = {
        customer_id: selectedClient?.id,
        status: selectedStatus?.id,
        gpStatus: selectedGPStatus?.id,
        vin: selectedVin?.id,
        lot: selectedLot?.id,
        container: getValues("container"),
        destination_id: selectedDestination?.id,
        from_date: toDateOnly(fromDate),
        to_date: toDateOnly(toDate),
      };
      if (fromDate && toDate) {
        getClientCosting(1, "", data);
        getVaultDashboard(1, "", { customer_id: selectedClient?.id });
        filterData();
      }
      return null;
    } catch (error) {
      ErrorToaster(error);
    }
  };

  // *For Get Vault Dashboard
  const getVaultDashboard = async (page, limit, filter) => {
    setLoader(true);
    try {
      const Page = page ? page : currentPage;
      const Limit = limit ? limit : pageLimit;
      const Filter = { ...filters, ...filter };
      setCurrentPage(Page);
      setPageLimit(Limit);
      setFilters(Filter);
      let params = {
        page: Page,
        limit: Limit,
      };
      params = { ...params, ...Filter };
      const {
        data: { detail },
      } = await FinanceServices.getVehicleSumLedgerV2(params);
      // console.log(detail, 'de');
      setShippingVault(
        detail?.rows[0].accounts?.find(
          (account) =>
            account.type_code === "L2" && account.primary_series === 50005,
        ),
      );
      const shipWallet = detail?.rows[0].accounts?.find(
        (account) =>
          account.type_code === "L2" && account.primary_series === 50005,
      );
      setCustomDue(
        detail?.rows[0].accounts?.find(
          (account) =>
            account.type_code === "A2" && account.primary_series === 20006,
        ),
      );
      const customWallet = detail?.rows[0].accounts?.find(
        (account) =>
          account.type_code === "A2" && account.primary_series === 20006,
      );

      setParkingDue(
        detail?.rows[0].accounts?.find(
          (account) =>
            account.type_code === "A2" && account.primary_series === 20004,
        ),
      );

      const parkingWallet = detail?.rows[0].accounts?.find(
        (account) =>
          account.type_code === "A2" && account.primary_series === 20004,
      );

      setVehicleVault(
        detail?.rows[0].accounts?.find(
          (account) =>
            account.type_code === "L2" && account.primary_series === 50004,
        ),
      );
      const exchangeWallet =
        parseFloat(shipWallet?.total_credit || 0) -
        parseFloat(shipWallet?.total_debit || 0);
      const vehWallet = detail?.rows[0].accounts?.find(
        (account) =>
          account.type_code === "L2" && account.primary_series === 50004,
      );
      setTotalShippingWallet(
        parseFloat(
          exchangeWallet && currencyExchangeRate
            ? exchangeWallet / currencyExchangeRate
            : 0,
        ).toFixed(2),
      );
      setTotalVehicleWallet(
        vehWallet?.nature === "credit"
          ? parseFloat(vehWallet?.total_credit - vehWallet?.total_debit)
          : parseFloat(vehWallet?.total_debit - vehWallet?.total_credit),
      );
      // const exchangeCustom = parseFloat(customWallet?.total_credit || 0) - parseFloat(customWallet?.total_debit || 0)
      setTotalCustomWallet(
        customWallet?.nature === "debit"
          ? parseFloat(customWallet?.total_debit).toFixed(2) -
              parseFloat(customWallet?.total_credit).toFixed(2)
          : parseFloat(customWallet?.total_credit - customWallet?.total_debit),
      );
      setTotalParkingWallet(
        parkingWallet?.nature === "debit"
          ? parseFloat(parkingWallet?.total_debit).toFixed(2) -
              parseFloat(parkingWallet?.total_credit).toFixed(2)
          : parseFloat(parkingWallet?.total_credit).toFixed(2) -
              parseFloat(parkingWallet?.total_debit).toFixed(2),
      );

      // setTotalCustomWallet(parseFloat(
      //   exchangeCustom / currencyExchangeRate
      // ).toFixed(2))
      setTotalCount(detail?.count);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const sortData = (e, type, item) => {
    e.preventDefault();

    if (type === "ascending" && item == "Buyer ID") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        // console.log(a, b);
        return a.booking?.buyer?.name.localeCompare(b.booking?.buyer?.name);
      });

      setShippingSOA(sortedData);
    }
    if (type === "descending" && item == "Buyer ID") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        return b.booking?.buyer?.name.localeCompare(a.booking?.buyer?.name);
      });

      setShippingSOA(sortedData);
    }
    if (type === "ascending" && item == "Buy Date") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        // console.log(a, b);
        return a.booking?.buyer?.name.localeCompare(b.booking?.buyer?.name);
      });

      setShippingSOA(sortedData);
    }
    if (type === "descending" && item == "Buy Date") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        return b.booking?.buyer?.name.localeCompare(a.booking?.buyer?.name);
      });

      setShippingSOA(sortedData);
    }
    if (type === "ascending" && item == "Status") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        // console.log(a, b);
        return a.booking?.buyer?.name.localeCompare(b.booking?.buyer?.name);
      });

      setShippingSOA(sortedData);
    }
    if (type === "descending" && item == "Status") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        return b.booking?.buyer?.name.localeCompare(a.booking?.buyer?.name);
      });

      setShippingSOA(sortedData);
    }

    if (type === "ascending" && item == "Model") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        // console.log(a, b);
        return a.booking?.veh_model?.name.localeCompare(
          b.booking?.veh_model?.name,
        );
      });

      setShippingSOA(sortedData);
    }

    if (type === "descending" && item == "Model") {
      // console.log("deefgghe");
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        return b.booking?.veh_model?.name.localeCompare(
          a.booking?.veh_model?.name,
        );
      });

      setShippingSOA(sortedData);
    }
    if (type === "ascending" && item == "Make") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        console.log(a, b);
        return a.booking?.veh_make?.name.localeCompare(
          b.booking?.veh_make?.name,
        );
      });

      setShippingSOA(sortedData);
    }
    if (type === "descending" && item == "Make") {
      console.log("deefgghe");
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        return b.booking?.veh_make?.name.localeCompare(
          a.booking?.veh_make?.name,
        );
      });

      setShippingSOA(sortedData);
    }

    if (type === "ascending" && item === "LOT#") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        const costA = parseFloat(a.booking?.lot_number) || 0;
        const costB = parseFloat(b.booking?.lot_number) || 0;
        console.log(costA, costB); // Add this line for debugging
        return costA - costB;
      });

      setShippingSOA(sortedData);
    }
    if (type === "descending" && item === "LOT#") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        const costA = parseFloat(a.booking?.lot_number) || 0;
        const costB = parseFloat(b.booking?.lot_number) || 0;
        // console.log(costA, costB); // Add this line for debugging
        return costB - costA;
      });

      setShippingSOA(sortedData);
    }

    if (type === "ascending" && item == "VIN#") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        // console.log(a, b);
        return a.booking?.vin.localeCompare(b.booking?.vin);
      });

      setShippingSOA(sortedData);
    }

    if (type === "descending" && item == "VIN#") {
      // console.log("deefgghe");
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        return b.booking?.vin.localeCompare(a.booking?.vin);
      });

      setShippingSOA(sortedData);
    }

    if (type === "ascending" && item == "Color") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        // console.log(a, b);
        return a.booking?.color.localeCompare(b.booking?.color);
      });

      setShippingSOA(sortedData);
    }

    if (type === "descending" && item == "Color") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        return b.booking?.color.localeCompare(a.booking?.color);
      });

      setShippingSOA(sortedData);
    }
    if (type === "ascending" && item == "Arrived Date") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        // console.log(a, b);
        return a.booking?.color.localeCompare(b.booking?.color);
      });

      setShippingSOA(sortedData);
    }

    if (type === "descending" && item == "Arrived Date") {
      const sortedData = [...shippingSOA].sort((a, b) => {
        // Use the localeCompare method for string comparison
        return b.booking?.color.localeCompare(a.booking?.color);
      });

      setShippingSOA(sortedData);
    }
  };

  const handleColumnChange = (event) => {
    const selectedColumns = event.target.value;
    // Sort the selected columns to maintain the correct order
    const sortedColumns = selectedColumns.sort((a, b) => a - b);
    setVisibleColumns(sortedColumns);
  };

  const renderCellContent = (
    index,
    colIndex,
    item,
    isActive,
    isExporting = false,
  ) => {
    const date = moment(item?.vehicle?.vcc?.vcc_expiry_date).format(
      "DD-MM-YYYY",
    );
    const targetDate = moment(date, "DD-MM-YYYY");
    let subtotalAmount =
      parseFloat(item?.invoice?.total) +
      parseFloat(item?.discount) +
      parseFloat(item?.war_surcharge_disc || 0);
    let daysRemaining = targetDate > moment();
    switch (colIndex) {
      case 0:
        return index + 1;
      // return item?.booking?.purchase_date
      //   ? moment(item?.booking?.purchase_date).format("DD-MMM-YYYY")
      //   : "-";
      case 1:
        return item?.booking?.purchase_date
          ? moment(item?.booking?.purchase_date).format("DD-MMM-YYYY")
          : "-";
      case 2:
        const make = item?.booking?.veh_make?.name ?? "";
        const model = item?.booking?.veh_model?.name ?? "";
        const color = item?.booking.color ?? "";
        const MakeModelColor = `${model} - ${make}-${color}`;
        if (isExporting) return MakeModelColor;
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={item?.booking?.veh_make?.name ?? "-"}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [10, -2],
                      },
                    },
                  ],
                },
              }}
            >
              {MakeModelColor > 12
                ? MakeModelColor?.slice(0, 12) + "..."
                : MakeModelColor}
            </Tooltip>
            <Box
              component={"div"}
              className="pdf-show"
              sx={{ display: "none !important" }}
            >
              {MakeModelColor ?? "-"}
            </Box>
          </Box>
        );
      case 3:
        if (isExporting) return item?.booking?.lot_number ?? "-";
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={copied ? "copied" : (item?.booking?.lot_number ?? "-")}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [10, -2],
                      },
                    },
                  ],
                },
              }}
              onClick={() => copyContent(item?.booking?.lot_number)}
            >
              {item?.booking?.lot_number?.length > 12
                ? item?.booking?.lot_number?.slice(0, 12) + "..."
                : item?.booking?.lot_number}
            </Tooltip>
            <Box
              component={"div"}
              className="pdf-show"
              sx={{ display: "none !important" }}
            >
              {item?.booking?.lot_number ?? "-"}
            </Box>
          </Box>
        );
      case 4:
        if (isExporting) return item?.booking?.vin ?? "-";
        return (
          <Box>
            <Tooltip
              className="pdf-hide"
              title={copied ? "copied" : (item?.booking?.vin ?? "-")}
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [10, -2],
                      },
                    },
                  ],
                },
              }}
              onClick={() => copyContent(item?.booking?.vin)}
            >
              {item?.booking?.vin?.length > 12
                ? item?.booking?.vin?.slice(0, 12) + "..."
                : item?.booking?.vin}
            </Tooltip>
            <Box
              component={"div"}
              className="pdf-show"
              sx={{ display: "none !important" }}
            >
              {item?.booking?.vin ?? "-"}
            </Box>
          </Box>
        );
      case 5:
        return item?.shipping?.dest?.name ?? "-";
      case 6:
        return item?.vehicle?.arrived_port_date
          ? moment(item?.vehicle?.arrived_port_date).format("DD-MMM-YYYY")
          : "-";
      case 7:
        return (
          <>
            {CommaSeparator(getShippingChargesTotal(item).toFixed(2)) ?? "-"}
          </>
        );
      case 8:
        return (
          <>
            {parseFloat(item?.late_fee) > 0
              ? CommaSeparator(parseFloat(item?.late_fee).toFixed(2))
              : "-"}
          </>
        );
      case 9:
        return (
          <>
            {parseFloat(item?.ship_late_fee) > 0
              ? CommaSeparator(parseFloat(item?.ship_late_fee).toFixed(2))
              : "-"}
          </>
        );
      case 10:
        return (
          <>
            {parseFloat(item?.storage) > 0
              ? CommaSeparator(parseFloat(item?.storage).toFixed(2))
              : "-"}
          </>
        );
      case 11:
        return (
          <>
            {parseFloat(item?.terminal_storages) > 0
              ? CommaSeparator(parseFloat(item?.terminal_storages).toFixed(2))
              : "-"}
          </>
        );
      case 12:
        return (
          <>
            {parseFloat(item?.category_a) > 0
              ? CommaSeparator(parseFloat(item?.category_a).toFixed(2))
              : "-"}
          </>
        );
      case 13:
        return (
          <>
            {parseFloat(item?.broker_fee) > 0
              ? CommaSeparator(parseFloat(item?.broker_fee).toFixed(2))
              : "-"}
          </>
        );
      case 14:
        return (
          <>
            {parseFloat(item?.title_fee) > 0
              ? CommaSeparator(parseFloat(item?.title_fee).toFixed(2))
              : "-"}
          </>
        );
      case 15: {
        const cutoff = moment("25-05-2025", "DD-MM-YYYY");

        const purchaseMoment = item?.createdAt ? moment(item?.createdAt) : null;

        const useV1 = purchaseMoment
          ? purchaseMoment.isSameOrBefore(cutoff, "day")
          : false;

        const value = useV1 ? item?.other_charge : item?.clearance_att_fee;
        return (
          <>
            {parseFloat(value) > 0
              ? CommaSeparator(parseFloat(value).toFixed(2))
              : "-"}
          </>
        );
      }

      // case 12:
      //   return (
      //     <>
      //       {parseFloat(item?.clearance_att_fee) > 0
      //         ? CommaSeparator(parseFloat(item?.clearance_att_fee).toFixed(2))
      //         : "-"}
      //     </>
      //   );

      case 16:
        return (
          <>
            {parseFloat(item?.current_suv) > 0
              ? CommaSeparator(parseFloat(item?.current_suv).toFixed(2))
              : "-"}
          </>
        );

      case 17:
        return <>{parseFloat(item?.hybrid_fee) > 0 ? item?.hybrid_fee : "-"}</>;

      case 18:
        return (
          <>
            {parseFloat(item?.heavy_equipment_fee) > 0
              ? item?.heavy_equipment_fee
              : "-"}
          </>
        );

      case 19:
        return (
          <>
            {parseFloat(item?.parts_pickup_fee) > 0
              ? item?.parts_pickup_fee
              : "-"}
          </>
        );

      case 20:
        return (
          <>{parseFloat(item?.wrapping_fee) > 0 ? item?.wrapping_fee : "-"}</>
        );

      case 21:
        return (
          <>
            {parseFloat(item?.relist_reversal_fee) > 0
              ? item?.relist_reversal_fee
              : "-"}
          </>
        );

      case 22:
        return <>{parseFloat(item?.misc_fee) > 0 ? item?.misc_fee : "-"}</>;

      case 23:
        return (
          <>
            {parseFloat(item?.fuel_surcharge) > 0 ? item?.fuel_surcharge : "-"}
          </>
        );

      case 24:
        return (
          <>{parseFloat(item?.color_change) > 0 ? item?.color_change : "-"}</>
        );

      case 25:
        return (
          <>{parseFloat(item?.thc_charges) > 0 ? item?.thc_charges : "-"}</>
        );
      case 26:
        return (
          <>
            {parseFloat(item?.intl_clearance) > 0
              ? CommaSeparator(parseFloat(item?.intl_clearance).toFixed(2))
              : "-"}
          </>
        );
      case 27:
        return (
          <>
            {parseFloat(item?.inland_transportation) > 0
              ? CommaSeparator(
                  parseFloat(item?.inland_transportation).toFixed(2),
                )
              : "-"}
          </>
        );
      case 28:
        return (
          <>
            {parseFloat(item?.intnl_export_charges) > 0
              ? CommaSeparator(
                  parseFloat(item?.intnl_export_charges).toFixed(2),
                )
              : "-"}
          </>
        );
      case 29:
        return (
          <>
            {parseFloat(item?.emergency_dep) > 0
              ? CommaSeparator(parseFloat(item?.emergency_dep).toFixed(2))
              : "-"}
          </>
        );

      case 30:
        return (
          <>
            {parseFloat(item?.invoice?.custom_total) > 0
              ? CommaSeparator(
                  parseFloat(item?.invoice?.custom_total).toFixed(2),
                )
              : "-"}
            {/* $
            {
              CommaSeparator(
                parseFloat(
                  (parseFloat(item?.shipping_charges || 0) +
                    parseFloat(item?.late_fee || 0) +
                    parseFloat(item?.storage || 0) +
                    parseFloat(item?.category_a || 0) +
                    parseFloat(item?.broker_fee || 0) +
                    parseFloat(item?.title_fee || 0) +
                    parseFloat(item?.other_charge || 0) +
                    parseFloat(item?.vehicle?.gate_pass?.details[0]?.parking_due || 0)
                  )).toFixed(2)
              ) ?? "-"
            } */}
          </>
        );
      case 31:
        return (
          <>
            {parseFloat(item?.invoice?.vat_total) > 0
              ? CommaSeparator(parseFloat(item?.invoice?.vat_total).toFixed(2))
              : "-"}
          </>
        );
      case 32:
        return (
          <>
            {" "}
            {CommaSeparator(parseFloat(subtotalAmount).toFixed(2)) ?? "-"}
            {/* {CommaSeparator(
              parseFloat(
                parseFloat(item?.shipping_charges || 0) +
                parseFloat(item?.late_fee || 0) +
                parseFloat(item?.storage || 0) +
                parseFloat(item?.category_a || 0) +
                parseFloat(item?.broker_fee || 0) +
                parseFloat(item?.title_fee || 0) +
                parseFloat(item?.other_charge || 0)
              ).toFixed(2)
            ) ?? "-"} */}
          </>
        );
      case 33:
        return (
          <>{CommaSeparator(parseFloat(item?.discount).toFixed(2)) ?? "-"}</>
        );
      case 34:
        return (
          <span style={{ color: Colors.danger }}>
            {CommaSeparator(
              parseFloat(item?.war_surcharge_disc || 0).toFixed(2),
            ) ?? "-"}
          </span>
        );
      case 35:
        return (
          <>
            {" "}
            {CommaSeparator(
              parseFloat(
                parseFloat(subtotalAmount).toFixed(2) -
                  parseFloat(item?.discount).toFixed(2) -
                  parseFloat(item?.war_surcharge_disc || 0).toFixed(2),
              ).toFixed(2),
            )}
          </>
        );
      case 36:
        return (
          <b>{CommaSeparator(parseFloat(item?.invoice?.paid).toFixed(2))}</b>

          // <Box
          //   component={"div"}
          //   className="pdf-hide"
          //   onClick={
          //     item?.invoice
          //       ? () =>
          //         navigate(`/client-invoice-detail/${item?.invoice?.id}`, {
          //           state: {
          //             shipping: true,
          //           },
          //         })
          //       : () => {
          //         ErrorToaster("Invoice Not Available");
          //       }
          //   }
          // >
          //   <IconButton
          //     sx={{
          //       bgcolor: Colors.primary,
          //       "&:hover": {
          //         bgcolor: Colors.primary,
          //       },
          //     }}
          //   >
          //     <EyeIcon />
          //   </IconButton>
          //   <Typography variant="body2">View</Typography>
          // </Box>
        );
      case 37:
        return (
          <b>{CommaSeparator(parseFloat(item?.invoice?.balance).toFixed(2))}</b>
        );

      case 38:
        return (
          <b>
            {moment().diff(item?.vehicle?.arrived_port_date, "days") || 0} Days
          </b>
        );

      case 39:
        return (
          <Box
            sx={{
              path: {
                fill:
                  item?.invoice?.balance && item?.invoice?.balance === "0.000"
                    ? Colors.primary
                    : item?.invoice?.balance !== "0.000"
                      ? Colors.danger
                      : Colors.bluishCyan,
              },
            }}
          >
            <span className="pdf-hide">
              {" "}
              {item?.vehicle?.arrived_port_date === null &&
              item?.vehicle?.loading_date ? (
                <PendingIcon />
              ) : (
                (() => {
                  const arrivedDate = moment(item?.vehicle?.arrived_port_date);
                  const dueThresholdDate = moment(arrivedDate).add(15, "days");
                  const today = moment();

                  if (
                    today.isSameOrAfter(dueThresholdDate, "day") &&
                    item?.invoice?.balance !== "0.000"
                  ) {
                    return <PendingIcon />;
                  }

                  if (item?.invoice?.balance === "0.000") {
                    return <CheckIcon />;
                  }

                  if (item?.invoice?.balance !== "0.000") {
                    return <PendingIcon />;
                  }

                  return <CheckIcon />;
                })()
              )}
            </span>
            <Typography variant="body2">
              {item?.vehicle?.arrived_port_date === null &&
              item?.vehicle?.loading_date
                ? "On The Way"
                : (() => {
                    const arrivedDate = moment(
                      item?.vehicle?.arrived_port_date,
                    );
                    const dueThresholdDate = moment(arrivedDate).add(
                      15,
                      "days",
                    );
                    const today = moment();
                    if (
                      today.isSameOrAfter(dueThresholdDate, "day") &&
                      item?.invoice?.balance !== "0.000"
                    ) {
                      return "Arrived And Due";
                    }

                    if (item?.invoice?.balance === "0.000") {
                      return "paid";
                    }
                    if (item?.invoice?.balance !== "0.000") {
                      return "Arrived And Due";
                    }

                    return "Partial";
                  })()}
            </Typography>
          </Box>
        );

      default:
        return "-";
    }
  };

  // const downloadExcel = () => {
  //   // Define headers and data separately
  //   const headers = tableHead.filter((item) => item !== "Action");
  //   const data = shippingSOA;

  //   // Extract values from objects and create an array for each row
  //   const rows = data.map((item, index) => {
  //     const date = moment(item?.vehicle?.vcc?.vcc_expiry_date).format(
  //       "DD-MM-YYYY"
  //     );
  //     const make = item?.booking?.veh_make?.name ?? "";
  //     const model = item?.booking?.veh_model?.name ?? "";
  //     const color = item?.booking.color ?? "";
  //     const MakeModelColor = `${make} - ${model}-${color}`;
  //     const vin = item?.booking?.vin ?? "-";
  //     const lot = item?.booking?.lot_number ?? "-";

  //     const customTotal = parseFloat(item?.invoice?.custom_total || 0).toFixed(
  //       2
  //     );
  //     const vatTotal = parseFloat(item?.invoice?.vat_total || 0).toFixed(2);
  //     const subTotal = (
  //       parseFloat(item?.shipping_charges || 0) +
  //       parseFloat(item?.late_fee || 0) +
  //       parseFloat(item?.storage || 0) +
  //       parseFloat(item?.category_a || 0) +
  //       parseFloat(item?.broker_fee || 0) +
  //       parseFloat(item?.title_fee || 0) +
  //       parseFloat(item?.other_charge || 0)
  //     ).toFixed(2);

  //     const netDue = (
  //       (totalBalance || 0) - Math.abs(totalShippingWallet * -1)
  //     ).toFixed(2);

  //     return [
  //       index + 1,
  //       item?.booking?.purchase_date
  //         ? moment(item?.booking?.purchase_date).format("DD-MMM-YYYY")
  //         : "-",
  //       MakeModelColor,
  //       lot,
  //       vin,
  //       item?.vehicle?.arrived_galaxy_date
  //         ? moment(item?.vehicle?.arrived_galaxy_date).format("DD-MMM-YYYY")
  //         : "-",
  //       parseFloat(item?.shipping_charges || 0).toFixed(2),
  //       parseFloat(item?.late_fee || 0).toFixed(2),
  //       parseFloat(item?.storage || 0).toFixed(2),
  //       parseFloat(item?.category_a || 0).toFixed(2),
  //       parseFloat(item?.broker_fee || 0).toFixed(2),
  //       parseFloat(item?.title_fee || 0).toFixed(2),
  //       parseFloat(item?.other_charge || 0).toFixed(2),
  //       item?.hybrid_fee ?? "-",
  //       item?.heavy_equipment_fee ?? "-",
  //       item?.parts_pickup_fee ?? "-",
  //       item?.wrapping_fee ?? "-",
  //       item?.relist_reversal_fee ?? "-",
  //       item?.misc_fee ?? "-",
  //       customTotal,
  //       vatTotal,
  //       subTotal,
  //       parseFloat(item?.discount || 0).toFixed(2),
  //       netDue,
  //       parseFloat(item?.invoice?.paid || 0).toFixed(2),
  //       parseFloat(item?.invoice?.balance || 0).toFixed(2),
  //       moment().diff(item?.createdAt, "days"),
  //       "-", // Notes Misc Fee
  //     ];
  //   });

  //   const totalRow1 = [
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "Total",
  //     `USD $${parseFloat(mainTotal).toFixed(2)}`,
  //     `USD $${parseFloat(totalPaid).toFixed(2)}`,
  //     `USD $${parseFloat(totalBalance).toFixed(2)}`,
  //     "",
  //   ];

  //   const totalRow2 = [
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "Vehicle Wallet Balance",
  //     `USD  $${CommaSeparator(
  //       parseFloat(totalVehicleWallet / currencyExchangeRate).toFixed(2)
  //     )}

  //     ).toFixed(2)}`,
  //     "",
  //     "",
  //     "",
  //   ];

  //   const totalRow3 = [
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "Shipping Wallet Balance",
  //     `USD  $${CommaSeparator(parseFloat(totalShippingWallet * -1).toFixed(2))}
  //    ).toFixed(2)}`,
  //     "",
  //     "",
  //     "",
  //   ];
  //   const totalRow4 = [
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "Balance After Wallet/Ledger Balance",
  //     `USD  $${CommaSeparator(
  //       parseFloat(
  //         parseFloat(totalBalance) - Math.abs(totalShippingWallet * -1)
  //       ).toFixed(2)
  //     )}
  //     ).toFixed(2)}`,
  //     "",
  //     "",
  //     "",
  //   ];
  //   const totalRow5 = [
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "",
  //     "Shipping Recieveable with All Custom & VAT",
  //     `USD  ${CommaSeparator(parseFloat(shippingReceivableCV).toFixed(2))}
  //     ).toFixed(2)}`,
  //     "",
  //     "",
  //     "",
  //   ];

  //   // Create a workbook with a worksheet
  //   const ws = XLSX.utils.aoa_to_sheet([
  //     headers,
  //     ...rows,
  //     totalRow1,
  //     totalRow2,
  //     totalRow3,
  //     totalRow4,
  //     totalRow5,
  //   ]);

  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

  //   // Convert the workbook to an array buffer
  //   const buf = XLSX.write(wb, {
  //     bookType: "xlsx",
  //     type: "array",
  //     mimeType:
  //       "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //   });

  //   // Save the file using FileSaver.js
  //   saveAs(new Blob([buf]), "data.xlsx");
  // };

  const extractText = (element) => {
    if (typeof element === "string" || typeof element === "number")
      return element;
    if (!element || !React.isValidElement(element)) return "-";

    const { children } = element.props || {};

    if (typeof children === "string" || typeof children === "number")
      return children;

    if (Array.isArray(children)) {
      return children
        .map((child) => {
          if (typeof child === "string" || typeof child === "number")
            return child;
          return extractText(child);
        })
        .join(" ");
    }

    if (React.isValidElement(children)) return extractText(children);

    return "";
  };

  const formatAccounting = (val) => {
    const num = Number(val) || 0;
    const pretty = CommaSeparator(Math.abs(num).toFixed(2));
    return num < 0 ? `(${pretty})` : pretty;
  };

  // const downloadExcel = () => {
  //   // Use visible columns only
  //   const headers = visibleColumns.map((index) => tableHead[index]);

  //   // Extract table data
  //   const rows = shippingSOA.map((item, rowIndex) => {
  //     return visibleColumns.map((colIndex) => {
  //       const content = renderCellContent(rowIndex, colIndex, item, true, true);
  //       return extractText(content);
  //     });
  //   });
  //   // Summary row (totals)
  //   const totalRow = visibleColumns.map((colIndex) => {
  //     const header = tableHead[colIndex];
  //     if (header.includes("Sub"))
  //       return `$${CommaSeparator(parseFloat(subTotal).toFixed(2))}`;
  //     if (header.includes("Net"))
  //       return `$${CommaSeparator(parseFloat(mainTotal).toFixed(2))}`;
  //     if (header.includes("Paid"))
  //       return `$${CommaSeparator(parseFloat(totalPaid).toFixed(2))}`;
  //     if (header.includes("Balance"))
  //       return `$${CommaSeparator(parseFloat(totalBalance).toFixed(2))}`;
  //     if (colIndex === 0) return "TOTAL";
  //     return "";
  //   });

  //   // Financial summary
  //   const financialSummary = [
  //     [],
  //     [
  //       "Total Parking Due (for Breakdown Download Gate Pass Report)",
  //       `$${formatAccounting((
  //         (totalParkingWallet && currencyExchangeRate)
  //           ? (totalParkingWallet / currencyExchangeRate)
  //           : 0).toFixed(2)
  //       )}`,
  //     ],
  //     [
  //       "Total Shipping Due (including Parking)",
  //       `$${formatAccounting(
  //         (
  //           parseFloat(totalBalance) +
  //           parseFloat((totalParkingWallet && currencyExchangeRate)
  //             ? (totalParkingWallet / currencyExchangeRate)
  //             : 0)).toFixed(2)
  //       )}`,
  //     ],
  //     [
  //       "Shipping Wallet Balance",
  //       `$${formatAccounting((((totalShippingWallet)
  //         ? (totalShippingWallet)
  //         : 0
  //       ) * -1).toFixed(2))}`,
  //     ],
  //     [
  //       "Due After Shipping Wallet/Ledger Balance",
  //       `$${formatAccounting(parseFloat(netDueTotal).toFixed(2))}`,
  //     ],
  //     [
  //       "Vehicle Wallet Balance",
  //       `$${formatAccounting(
  //         (((totalVehicleWallet && currencyExchangeRate)
  //           ? (totalVehicleWallet / currencyExchangeRate)
  //           : 0
  //         ) * -1).toFixed(2)
  //       )}`,
  //     ],
  //     [
  //       "Net Due(After Shipping And Vehicle Wallet Balance)",
  //       `$${formatAccounting(
  //         parseFloat(
  //           parseFloat(netDueTotal) +
  //           parseFloat((
  //             (totalVehicleWallet && currencyExchangeRate)
  //               ? (totalVehicleWallet / currencyExchangeRate)
  //               : 0
  //           ) * -1)
  //         ).toFixed(2)
  //       )}`,
  //     ],
  //     [
  //       "undue Custom (Not Charged Yet)",
  //       `$${formatAccounting(
  //         parseFloat((totalCustomWallet && currencyExchangeRate)
  //           ? (totalCustomWallet / currencyExchangeRate)
  //           : 0
  //         ).toFixed(2)
  //       )}`,
  //     ],
  //     [
  //       "Shipping Recieveable with undue Custom",
  //       `$${formatAccounting(
  //         parseFloat(
  //           parseFloat(netDueTotal) +
  //           parseFloat((
  //             (totalVehicleWallet && currencyExchangeRate)
  //               ? (totalVehicleWallet / currencyExchangeRate)
  //               : 0
  //           ) * -1) +
  //           parseFloat(
  //             (totalCustomWallet && currencyExchangeRate)
  //               ? (totalCustomWallet / currencyExchangeRate)
  //               : 0
  //           )
  //         ).toFixed(2)
  //       )}`,
  //     ],
  //   ];

  //   // Ageing summary rows
  //   const ageingRows = [
  //     ["Ageing Summary"],
  //     ["Below 15 Days", `$${CommaSeparator(dueBreakup?.below15DaysTotal)}`],
  //     ["16 - 30 Days", `$${CommaSeparator(dueBreakup?.below16To30DaysTotal)}`],
  //     ["Over 30 Days", `$${CommaSeparator(dueBreakup?.over30DaysTotal)}`],
  //     ["Total", `$${CommaSeparator(dueBreakup?.total)}`],
  //   ];

  //   // Merge all together into the worksheet
  //   const ws = XLSX.utils.aoa_to_sheet([
  //     headers,
  //     ...rows,
  //     [],
  //     totalRow,
  //     [],
  //     ...financialSummary,
  //     [],
  //     ...ageingRows,
  //   ]);

  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, "Shipping SOA");
  //   const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  //   saveAs(new Blob([buf]), "Shipping-SOA.xlsx");
  // };

  useEffect(() => {
    if (paymentType) {
      const cashierDetail = cashierAccounts.find(
        (e) => e?.currency === paymentType,
      );
      setValue("cash", cashierDetail?.name);
      setSelectedCashierAccount(cashierDetail);
    }
  }, [paymentType]);

  useEffect(() => {
    getClientDropdown();
    getCurrencies();
    getDestinations();
  }, []);

  // *For Get Destinations (Destination filter options)
  const getDestinations = async (search) => {
    try {
      const { data } = await SystemServices.getDestinations({
        page: 1,
        limit: 200,
        search: search,
      });
      setDestinations(data?.destinations?.rows ?? []);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const getDateDifference = (date1, date2) => {
    const diffTime = Math.abs(date2 - date1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // console.log(filteredData, 'filter data')
  // const calculateDueBreakup = () => {

  //   const below15Days = shippingSOA?.filter(
  //     (item) =>
  //       getDateDifference(new Date(item?.vehicle?.arrived_port_date), new Date()) <= 15
  //   );

  //   const below16To30Days = shippingSOA?.filter(
  //     (item) =>
  //       getDateDifference(new Date(item?.vehicle?.arrived_port_date), new Date()) >
  //       15 &&
  //       getDateDifference(new Date(item?.vehicle?.arrived_port_date), new Date()) <=
  //       30
  //   );

  //   const over30Days = shippingSOA?.filter(
  //     (item) =>
  //       getDateDifference(new Date(item?.vehicle?.arrived_port_date), new Date()) > 30
  //   );

  //   const below15DaysTotal = below15Days?.reduce(
  //     (acc, curr) => acc + parseFloat(curr?.invoice?.balance),
  //     0
  //   );
  //   const below16To30DaysTotal = below16To30Days?.reduce(
  //     (acc, curr) => acc + parseFloat(curr?.invoice?.balance),
  //     0
  //   );
  //   const over30DaysTotal = over30Days?.reduce(
  //     (acc, curr) => acc + parseFloat(curr?.invoice?.balance),
  //     0
  //   );

  //   const below15DaysCount = below15Days?.length || 0;
  //   const below16To30DaysCount = below16To30Days?.length || 0;
  //   const over30DaysCount = over30Days?.length || 0;

  //   return {
  //     below15DaysTotal,
  //     below16To30DaysTotal,
  //     over30DaysTotal,
  //     below15DaysCount,
  //     below16To30DaysCount,
  //     over30DaysCount,
  //     totalCount: below15DaysCount + below16To30DaysCount + over30DaysCount,

  //     total: below15DaysTotal + below16To30DaysTotal + over30DaysTotal,
  //   };
  // };

  const calculateDueBreakup = () => {
    const below15Days = shippingSOA?.filter(
      (item) =>
        item?.invoice?.balance > 0 &&
        getDateDifference(
          new Date(item?.vehicle?.arrived_galaxy_date),
          new Date(),
        ) <= 15,
    );

    const below16To30Days = shippingSOA?.filter(
      (item) =>
        item?.invoice?.balance > 0 &&
        getDateDifference(
          new Date(item?.vehicle?.arrived_galaxy_date),
          new Date(),
        ) > 15 &&
        getDateDifference(
          new Date(item?.vehicle?.arrived_galaxy_date),
          new Date(),
        ) <= 30,
    );

    const over30Days = shippingSOA?.filter(
      (item) =>
        item?.invoice?.balance > 0 &&
        getDateDifference(
          new Date(item?.vehicle?.arrived_galaxy_date),
          new Date(),
        ) > 30,
    );

    const below15DaysTotal = below15Days?.reduce(
      (acc, curr) => acc + parseFloat(curr?.invoice?.balance),
      0,
    );
    const below16To30DaysTotal = below16To30Days?.reduce(
      (acc, curr) => acc + parseFloat(curr?.invoice?.balance),
      0,
    );
    const over30DaysTotal = over30Days?.reduce(
      (acc, curr) => acc + parseFloat(curr?.invoice?.balance),
      0,
    );

    const below15DaysCount = below15Days?.length || 0;
    const below16To30DaysCount = below16To30Days?.length || 0;
    const over30DaysCount = over30Days?.length || 0;

    return {
      below15DaysTotal,
      below16To30DaysTotal,
      over30DaysTotal,
      below15DaysCount,
      below16To30DaysCount,
      over30DaysCount,
      totalCount: below15DaysCount + below16To30DaysCount + over30DaysCount,
      total: below15DaysTotal + below16To30DaysTotal + over30DaysTotal,
    };
  };

  const dueBreakup = calculateDueBreakup();

  const netDueTotal = parseFloat(
    parseFloat(totalBalance) +
      parseFloat(
        totalParkingWallet && currencyExchangeRate
          ? totalParkingWallet / currencyExchangeRate
          : 0,
      ) +
      parseFloat(totalShippingWallet * -1),
  ).toFixed(2);

  // Single source of truth for "Net Due (After Shipping And Vehicle Wallet
  // Balance)" — used both by the PDF cell and by window.__SOA_PAYABLE__ so
  // the WhatsApp body is bit-identical to the printed PDF.
  const netDueAfterWallets = parseFloat(
    parseFloat(netDueTotal) +
      parseFloat(
        (totalVehicleWallet && currencyExchangeRate
          ? totalVehicleWallet / currencyExchangeRate
          : 0) * -1,
      ),
  ).toFixed(2);

  const cutoffDate = moment("25-05-2025", "DD-MM-YYYY");
  const columnValueGetters = {
    7: (item) => getShippingChargesTotal(item),
    8: (item) => +item?.late_fee || 0,
    9: (item) => +item?.ship_late_fee || 0,
    10: (item) => +item?.storage || 0,
    11: (item) => +item?.terminal_storages || 0,
    12: (item) => +item?.category_a || 0,
    13: (item) => +item?.broker_fee || 0,
    14: (item) => +item?.title_fee || 0,
    15: (item) => {
      const purchaseMoment = item?.createdAt ? moment(item?.createdAt) : null;
      const useV1 = purchaseMoment
        ? purchaseMoment.isSameOrBefore(cutoffDate, "day")
        : false;
      return useV1 ? +item?.other_charge || 0 : +item?.clearance_att_fee || 0;
    },
    16: (item) => +item?.current_suv || 0,
    17: (item) => +item?.hybrid_fee || 0,
    18: (item) => +item?.heavy_equipment_fee || 0,
    19: (item) => +item?.parts_pickup_fee || 0,
    20: (item) => +item?.wrapping_fee || 0,
    21: (item) => +item?.relist_reversal_fee || 0,
    22: (item) => +item?.misc_fee || 0,
    23: (item) => +item?.fuel_surcharge || 0,
    24: (item) => +item?.color_change || 0,
    25: (item) => +item?.thc_charges || 0,
    26: (item) => +item?.intl_clearance || 0,
    27: (item) => +item?.inland_transportation || 0,
    28: (item) => +item?.intnl_export_charges || 0,
    29: (item) => +item?.emergency_dep || 0,
    30: (item) => +item?.invoice?.custom_total || 0,
    31: (item) => +item?.invoice?.vat_total || 0,
  };

  const computePdfVisibleColumns = (rows, currentVisibleColumns) => {
    if (!Array.isArray(rows) || rows.length === 0) return currentVisibleColumns;

    const zeroColumns = Object.keys(columnValueGetters)
      .map(Number)
      .filter((colIndex) =>
        rows.every(
          (it) => (Number(columnValueGetters[colIndex](it)) || 0) === 0,
        ),
      );

    // Keep original order, just drop all-zero columns
    return currentVisibleColumns.filter((idx) => !zeroColumns.includes(idx));
  };

  // Safety gate shared by the PDF and Excel exporters. Blocks the export if
  //   - a fetch is still in flight (`loader`)
  //   - another export is already running (`isGeneratingPdf` / Excel)
  //   - there's no data yet
  //   - the customer whose data is on screen doesn't match the customer
  //     currently selected in the dropdown (race between "switch customer"
  //     and "click Download"). Returns true when safe to proceed.
  const canExportNow = () => {
    if (loader || isGeneratingPdf || isDownloadingExcel) return false;
    if (!shippingSOA || shippingSOA.length === 0) return false;
    if (!selectedClient?.id) return false;
    // If the data on screen was loaded for a different customer, refuse —
    // the user must click Search again before downloading.
    if (loadedCustomerId != null && loadedCustomerId !== selectedClient.id) {
      return false;
    }
    return true;
  };

  const handlePdfExport = () => {
    if (!canExportNow()) return;
    // 1) Compute which columns to show in the PDF (auto-hide all-zero columns)
    const filteredForPdf = computePdfVisibleColumns(
      shippingSOA || [],
      visibleColumns,
    );
    setPdfVisibleColumns(filteredForPdf);

    // 2) Export after React applies the state (micro delay is fine)
    setIsGeneratingPdf(true);
    setTimeout(() => {
      handleExportWithComponent(contentRef);
      setIsGeneratingPdf(false);
    }, 0);
  };
const downloadExcel = async () => {
  if (!canExportNow()) return;
  if (!shippingSOA || shippingSOA.length === 0) return;
  setIsDownloadingExcel(true);
  try {

  const excelVisibleColumns = computePdfVisibleColumns(
    shippingSOA,
    visibleColumns,
  );
  const colCount = excelVisibleColumns.length;
  const headers = excelVisibleColumns.map((idx) => tableHead[idx]);

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Shipping SOA", {
    views: [{ state: "frozen", ySplit: 0 }],
  });

  // ── Theme colors ────────────────────────────────────────────────────
  const COLOR_PRIMARY = "FF0C6135"; // dark green (header band, table header)
  const COLOR_ACCENT = "FF4AB43E"; // bright green (banner, paid status)
  const COLOR_STRIPE = "FFEFF8E7"; // light green (alt rows, footer)
  const COLOR_DUE = "FFFFABAB"; // red (arrived & due)
  const COLOR_WAY = "FF6BC5ED"; // blue (on the way)
  const COLOR_WHITE = "FFFFFFFF";

  const thinBorder = {
    top: { style: "thin", color: { argb: "FFB2B5BA" } },
    left: { style: "thin", color: { argb: "FFB2B5BA" } },
    bottom: { style: "thin", color: { argb: "FFB2B5BA" } },
    right: { style: "thin", color: { argb: "FFB2B5BA" } },
  };
  const applyBorder = (row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = thinBorder;
    });
  };
  const fill = (argb) => ({
    type: "pattern",
    pattern: "solid",
    fgColor: { argb },
  });

  // Anchor the ageing mini-table to the last 5 visible columns, so it never
  // overlaps the customer info block regardless of how many columns are shown.
  const ageingStartCol = Math.max(2, colCount - 4);

  // ── 0. Logo image, embedded over the banner rows ────────────────────
// ── 0. Logo image, spanning the full width of the table ────────────
let logoImageId = null;
let logoAspectRatio = null;
try {
  const response = await fetch(ShippingLandscapeLogo);
  const blob = await response.blob();
  const arrayBuffer = await blob.arrayBuffer();
  logoImageId = workbook.addImage({
    buffer: arrayBuffer,
    extension: "png",
  });

  // Get natural dimensions so we can scale height proportionally to the
  // full-width placement (avoids a stretched/squashed logo).
  const dims = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => resolve({ width: 1200, height: 150 }); // fallback ratio
    img.src = URL.createObjectURL(blob);
  });
  logoAspectRatio = dims.height / dims.width;
} catch (err) {
  console.error("[excel-export] failed to load logo:", err);
}

// Approximate the full table width in pixels, based on column widths set
// later (42 for col 1, 14 for the rest) — Excel's default char-to-pixel
// ratio is roughly 7px per character unit.
const PX_PER_COL_UNIT = 7;
const totalWidthPx =
  (42 + 14 * Math.max(colCount - 1, 0)) * PX_PER_COL_UNIT;
const logoHeightPx = logoAspectRatio
  ? Math.round(totalWidthPx * logoAspectRatio)
  : 60;

if (logoImageId !== null) {
  sheet.addImage(logoImageId, {
    tl: { col: 0, row: 0 },
    ext: { width: totalWidthPx, height: logoHeightPx },
    editAs: "oneCell",
  });
}

// Reserve enough blank rows for the image to sit in, sized to its height.
// Default Excel row height is ~20px, so divide to get a row count, then
// add a blank row per unit so nothing overlaps the image.
const logoRowCount = Math.max(2, Math.ceil(logoHeightPx / 20));
for (let i = 0; i < logoRowCount; i++) {
  sheet.addRow([]);
}
  // ── 1. Green title band (mirrors the PDF's banner) ─────────────────


  // ── 2. Sub-title + generated date row ──────────────────────────────
  const subTitleRow = sheet.addRow([
    "Shipping-Statement Of Account",
    ...new Array(Math.max(colCount - 2, 0)).fill(""),
    `Generated on: ${moment().format("DD-MM-YYYY")}`,
  ]);
  sheet.mergeCells(subTitleRow.number, 1, subTitleRow.number, Math.max(colCount - 1, 1));
  subTitleRow.getCell(1).font = { bold: true, size: 11 };
  subTitleRow.getCell(1).alignment = { horizontal: "center" };
  subTitleRow.getCell(colCount).font = { italic: true, size: 9 };
  subTitleRow.getCell(colCount).alignment = { horizontal: "right" };

  sheet.addRow([]); // spacer

  // ── 3. Customer Info (left) + Ageing table (right) ─────────────────
  const infoStartRow = sheet.rowCount + 1;

  const nameRow = sheet.addRow([
    "Name:",
    `${selectedClient?.id ?? "-"} - ${selectedClient?.name ?? "-"}`,
  ]);
  nameRow.getCell(1).font = { bold: true, size: 9 };
  nameRow.getCell(2).font = { bold: true, size: 10 };

  const periodRow = sheet.addRow([
    "Period:",
    `${moment(fromDate).format("DD-MM-YYYY")} - ${moment(toDate).format("DD-MM-YYYY")}`,
  ]);
  periodRow.getCell(1).font = { bold: true, size: 9 };
  periodRow.getCell(2).font = { size: 9 };

  const typeRow = sheet.addRow(["Type:", selectedStatus?.name ?? "All"]);
  typeRow.getCell(1).font = { bold: true, size: 9 };
  typeRow.getCell(2).font = { size: 9 };

  // Ageing mini-table — anchored to the last 5 columns.
  const ageingHeaderRow = sheet.getRow(infoStartRow);
  const ageingLabels = ["Days", "Below 15", "Btw 16-30", "Over 30", "Total"];
  ageingLabels.forEach((label, i) => {
    const cell = ageingHeaderRow.getCell(ageingStartCol + i);
    cell.value = label;
    cell.font = { bold: true, size: 9, color: { argb: COLOR_WHITE } };
    cell.fill = fill(COLOR_PRIMARY);
    cell.alignment = { horizontal: "center" };
    cell.border = thinBorder;
  });

  const ageingCountRow = sheet.getRow(infoStartRow + 1);
  const countValues = [
    "Veh. Count",
    Number(dueBreakup?.below15DaysCount ?? 0),
    Number(dueBreakup?.below16To30DaysCount ?? 0),
    Number(dueBreakup?.over30DaysCount ?? 0),
    Number(dueBreakup?.totalCount ?? 0),
  ];
  countValues.forEach((val, i) => {
    const cell = ageingCountRow.getCell(ageingStartCol + i);
    cell.value = val;
    cell.font = i === 0 ? { bold: true, size: 9, color: { argb: COLOR_WHITE } } : { size: 9 };
    if (i === 0) cell.fill = fill(COLOR_PRIMARY);
    cell.alignment = { horizontal: "center" };
    cell.border = thinBorder;
  });

  const ageingAmountRow = sheet.getRow(infoStartRow + 2);
  const amountValues = [
    "Amount $",
    Number(parseFloat(dueBreakup?.below15DaysTotal || 0).toFixed(2)),
    Number(parseFloat(dueBreakup?.below16To30DaysTotal || 0).toFixed(2)),
    Number(parseFloat(dueBreakup?.over30DaysTotal || 0).toFixed(2)),
    Number(parseFloat(dueBreakup?.total || 0).toFixed(2)),
  ];
  amountValues.forEach((val, i) => {
    const cell = ageingAmountRow.getCell(ageingStartCol + i);
    cell.value = val;
    cell.font = i === 0 ? { bold: true, size: 9, color: { argb: COLOR_WHITE } } : { size: 9 };
    if (i === 0) cell.fill = fill(COLOR_PRIMARY);
    if (typeof cell.value === "number") cell.numFmt = "#,##0.00";
    cell.alignment = { horizontal: "center" };
    cell.border = thinBorder;
  });

  sheet.addRow([]); // spacer

  // ── 4. Table header row ─────────────────────────────────────────────
  const headerRow = sheet.addRow(headers);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, size: 9, color: { argb: COLOR_WHITE } };
    cell.fill = fill(COLOR_PRIMARY);
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  });
  applyBorder(headerRow);

  // ── 5. Data rows (with status-style coloring on relevant cols) ─────
  const statusLabelIdx = excelVisibleColumns.findIndex(
    (idx) => tableHead[idx] === "Shipping Status",
  );
  const balanceLabelIdx = excelVisibleColumns.findIndex(
    (idx) => tableHead[idx] === "Balance Due",
  );
  const pastDueLabelIdx = excelVisibleColumns.findIndex(
    (idx) => tableHead[idx] === "Past Due Days",
  );

  // Numeric/amount columns — their values are written as real numbers (not
  // formatted strings) so Excel does not flag them with the green
  // "Number Stored as Text" / "Convert to Number" warning. Identifier columns
  // like LOT/VIN are intentionally excluded so they keep their exact text.
  const amountColumns = new Set([
    7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25,
    26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36,
  ]);

  shippingSOA.forEach((item, rowIndex) => {
    const rowValues = excelVisibleColumns.map((colIndex) => {
      const content = renderCellContent(rowIndex, colIndex, item, true, true);
      const text = extractText(content);
      if (amountColumns.has(colIndex)) {
        const normalized = String(text ?? "")
          .replace(/,/g, "")
          .trim();
        if (
          normalized !== "" &&
          normalized !== "-" &&
          Number.isFinite(Number(normalized))
        ) {
          return Number(normalized);
        }
      }
      return text;
    });
    const row = sheet.addRow(rowValues);

    const arrivedDate = moment(item?.vehicle?.arrived_port_date);
    const dueThresholdDate = moment(arrivedDate).add(15, "days");
    const today = moment();
    const isOverdue =
      today.isSameOrAfter(dueThresholdDate, "day") &&
      item?.invoice?.balance !== "0.000";
    const onTheWay =
      item?.vehicle?.arrived_port_date === null && item?.vehicle?.loading_date;
    const isPaid = item?.invoice?.balance === "0.000";

    let statusColor = null;
    if (isOverdue) statusColor = COLOR_DUE;
    else if (isPaid) statusColor = COLOR_ACCENT;
    else if (onTheWay) statusColor = COLOR_WAY;

    row.eachCell((cell, colNum) => {
      cell.alignment = { horizontal: "center" };
      cell.font = { size: 8 };
      const idx = colNum - 1;
      const colIndex = excelVisibleColumns[idx];
      if (amountColumns.has(colIndex) && typeof cell.value === "number") {
        cell.numFmt = "#,##0.00";
      }
      if (
        statusColor &&
        (idx === statusLabelIdx || idx === balanceLabelIdx || idx === pastDueLabelIdx)
      ) {
        cell.fill = fill(statusColor);
      } else if (rowIndex % 2 !== 0) {
        cell.fill = fill(COLOR_STRIPE);
      }
    });
    applyBorder(row);
  });

  // ── 6. Totals row ───────────────────────────────────────────────────
  const totalRowValues = new Array(colCount).fill("");
  totalRowValues[0] = "Total";
  excelVisibleColumns.forEach((colIdx, i) => {
    const header = tableHead[colIdx];
    if (header === "Sub Total")
      totalRowValues[i] = Number(parseFloat(subTotal).toFixed(2));
    if (header === "Net Due")
      totalRowValues[i] = Number(parseFloat(mainTotal).toFixed(2));
    if (header === "Paid")
      totalRowValues[i] = Number(parseFloat(totalPaid).toFixed(2));
    if (header === "Balance Due")
      totalRowValues[i] = Number(parseFloat(totalBalance).toFixed(2));
  });
  const totalRow = sheet.addRow(totalRowValues);
  totalRow.eachCell((cell) => {
    cell.font = { bold: true, size: 9 };
    cell.alignment = { horizontal: "center" };
    if (typeof cell.value === "number") cell.numFmt = "#,##0.00";
  });
  applyBorder(totalRow);

  // ── 7. Financial summary block ──────────────────────────────────────
  const totalParkingDue = parseFloat(
    totalParkingWallet && currencyExchangeRate
      ? totalParkingWallet / currencyExchangeRate
      : 0,
  ).toFixed(2);
  const shippingWalletBalance = parseFloat(
    (totalShippingWallet ? totalShippingWallet : 0) * -1,
  ).toFixed(2);
  const vehicleWalletBalance = parseFloat(
    (totalVehicleWallet && currencyExchangeRate
      ? totalVehicleWallet / currencyExchangeRate
      : 0) * -1,
  ).toFixed(2);
  const undueCustom = parseFloat(
    totalCustomWallet && currencyExchangeRate
      ? totalCustomWallet / currencyExchangeRate
      : 0,
  ).toFixed(2);
  const shippingReceivableWithUndueCustom = parseFloat(
    parseFloat(netDueTotal) +
      parseFloat(vehicleWalletBalance) +
      parseFloat(undueCustom),
  ).toFixed(2);

  const addSummaryRow = (label, value, formula) => {
    const row = sheet.addRow([]);
    // Merge the description across the left columns and right-align it so the
    // label sits immediately to the left of its amount (mirrors the PDF /
    // listing layout) instead of being stranded at the far-left of the sheet.
    if (colCount > 2) {
      sheet.mergeCells(row.number, 1, row.number, colCount - 1);
    }
    const labelCell = row.getCell(1);
    labelCell.value = label;
    labelCell.font = { bold: true, size: 9 };
    labelCell.alignment = { horizontal: "right" };

    const valueCell = row.getCell(colCount);
    // Store a real number (no "$", and negatives shown as "-123.45" via the
    // number format rather than "(123.45)") so Excel does not flag it as text.
    valueCell.value = formula
      ? { formula, result: Number(value) || 0 }
      : Number(value) || 0;
    valueCell.font = { bold: true, size: 9 };
    valueCell.alignment = { horizontal: "center" };
    valueCell.numFmt = "#,##0.00";
    applyBorder(row);
    return row;
  };

  sheet.addRow([]);
  const parkingRow = addSummaryRow(
    "Total Parking Due (for Breakdown Download Gate Pass Report)",
    totalParkingDue,
  );
  const shippingWalletRow = addSummaryRow(
    "Shipping Wallet Balance",
    shippingWalletBalance,
  );
  const vehicleWalletRow = addSummaryRow(
    "Vehicle Wallet Balance",
    vehicleWalletBalance,
  );

  // Net Due = Balance Due total + Total Parking Due + Shipping Wallet Balance
  // + Vehicle Wallet Balance. Written as a live Excel formula referencing the
  // cells above so it always matches them; each referenced cell already carries
  // its own +/- sign, so a plain sum honours the negative balances.
  const balanceTotalAddr =
    balanceLabelIdx >= 0 ? totalRow.getCell(balanceLabelIdx + 1).address : null;
  const netDueFormula = balanceTotalAddr
    ? `${balanceTotalAddr}+${parkingRow.getCell(colCount).address}+${shippingWalletRow.getCell(colCount).address}+${vehicleWalletRow.getCell(colCount).address}`
    : null;
  addSummaryRow(
    "Net Due (After Shipping And Vehicle Wallet Balance)",
    netDueAfterWallets,
    netDueFormula,
  );
  addSummaryRow("Undue Custom (Not Charged Yet)", undueCustom);
  addSummaryRow(
    "Shipping Receiveable with Undue Custom",
    shippingReceivableWithUndueCustom,
  );

  // ── 8. Footer notes (mirrors the PDF bottom notice block) ──────────
  sheet.addRow([]);
  const footerNotes = [
    "1- This is a system-generated statement and does not require a signature. Any discrepancies in this SOA should be reported within 2 days.",
    "2- Relist payments should be made on the next day of the relist. Otherwise, your account will be Locked on the second day and a 15% penalty will be charged on relists.",
    "3- Kindly settle the payment due at the earliest, we would like to remind you to be in touch with our customer service support team.",
    "4- GALAXY SHALL NOT BE RESPONSIBLE FOR YOUR CONTAINERS STORAGE AND DETENTION IN PORTS",
  ];
  footerNotes.forEach((note) => {
    const row = sheet.addRow([note]);
    sheet.mergeCells(row.number, 1, row.number, colCount);
    row.getCell(1).font = { size: 8, bold: true };
    row.getCell(1).fill = fill(COLOR_STRIPE);
  });

  // ── Column widths ────────────────────────────────────────────────────
  // The first column only holds the Sr. No now — the long summary labels are
  // merged across the row, so column 1 no longer needs to be wide.
  sheet.columns.forEach((col, i) => {
    col.width = i === 0 ? 8 : 14;
  });

  // ── Write & download ─────────────────────────────────────────────────
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `Shipping-SOA-${selectedClient?.name ?? "export"}.xlsx`,
  );
  } finally {
    setIsDownloadingExcel(false);
  }
};
  // ─── PRINT MODE (SOA WhatsApp cron) ──────────────────────────────────────
  // Once the clients dropdown + currencies are loaded, auto-pick the customer
  // from the URL.
  useEffect(() => {
    if (!printMode) return;
    if (selectedClient) return;
    if (!Array.isArray(clients) || clients.length === 0) return;
    if (!currencyExchangeRate) return;
    const match = clients.find(
      (c) => Number(c?.id) === Number(printCustomerId),
    );
    if (!match) {
      console.error(
        `[print] customer id ${printCustomerId} not in dropdown (${clients.length} clients)`,
      );
      return;
    }
    setSelectedClient(match);
  }, [
    printMode,
    printCustomerId,
    clients,
    currencyExchangeRate,
    selectedClient,
  ]);

  // Once selectedClient is set in print mode, fire the data fetches.
  // status: "false" = unpaid only (matches the SOA business intent: the cron
  // is reminding customers about outstanding balances, not reprinting their
  // full ledger). Also flip selectedStatus so the PDF's "Type:" label reads
  // "Unpaid" instead of the default "All".
  useEffect(() => {
    if (!printMode) return;
    if (!selectedClient) return;
    setSelectedStatus({ id: "false", name: "Unpaid" });
    const data = {
      customer_id: selectedClient.id,
      status: "false",
      from_date: toDateOnly(fromDate),
      to_date: toDateOnly(toDate),
    };
    getClientCosting(1, "", data);
    getVaultDashboard(1, "", { customer_id: selectedClient.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printMode, selectedClient]);

  // Auto-trim all-zero columns once data lands (mirrors handlePdfExport).
  useEffect(() => {
    if (!printMode) return;
    if (!shippingSOA || shippingSOA.length === 0) return;
    const filtered = computePdfVisibleColumns(shippingSOA, visibleColumns);
    setPdfVisibleColumns(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printMode, shippingSOA]);

  // Print-mode kendo export: once data is loaded, ask the kendo PDFExport for
  // its drawing group and convert it to a PDF data URI via @progress/kendo-drawing.
  // Puppeteer reads window.__SOA_PDF_DATA_URI__ instead of generating its own
  // PDF — this guarantees the cron output is byte-identical to the in-app
  // "Download PDF" button (same kendo paperSize, scale, and rendering pipeline).
  const printExportRef = useRef(null);
  // Captures any /clients/costing fetch error during print mode so the kendo
  // export can short-circuit and report it via window.__SOA_ERROR__.
  const [printFetchError, setPrintFetchError] = useState(null);
  useEffect(() => {
    if (!printMode) return;
    if (printFetchError) {
      window.__SOA_ERROR__ = `Shipping SOA fetch failed: ${printFetchError}`;
      window.__SOA_READY__ = true;
      return;
    }
    if (loader) return;
    if (!shippingSOA) return;

    let cancelled = false;
    const expectedRowCount = Array.isArray(shippingSOA)
      ? shippingSOA.length
      : 0;
    // Wait until the kendo subtree actually has the expected number of <tr>
    // rows (and the initial 800ms pass). Without this, drawDOM can fire
    // before MUI/emotion finishes painting a 100+ row table and we capture
    // empty cells. Polls every 150ms up to ~10s.
    const waitForRows = async (rootEl) => {
      const start = Date.now();
      while (Date.now() - start < 10000) {
        const rows = rootEl.querySelectorAll("tbody tr");
        if (rows.length >= expectedRowCount) return;
        await new Promise((r) => setTimeout(r, 150));
      }
    };
    const t = setTimeout(async () => {
      if (cancelled) return;
      if (!printExportRef.current) {
        window.__SOA_ERROR__ = "PDFExport ref not mounted";
        window.__SOA_READY__ = true;
        return;
      }
      try {
        // PDFExport exposes only .save() (which downloads). Reach into its
        // rootElForPDF (the wrapped div) and run drawDOM + exportPDF ourselves
        // with the same options the live page uses — produces an identical
        // PDF without triggering a browser download.
        const rootEl = printExportRef.current.rootElForPDF;
        if (!rootEl) {
          throw new Error("PDFExport rootElForPDF not mounted");
        }
        await waitForRows(rootEl);
        if (cancelled) return;
        const drawing = await import("@progress/kendo-drawing");
        // Mirror what kendo's KendoDrawingAdapter.savePDF does internally:
        // pass options to BOTH drawDOM and exportPDF (multiPage on the PDF
        // side is critical for multi-page SOAs like shipping).
        const drawOpts = {
          paperSize: ["210mm", "400mm"],
          landscape: true,
          scale: 0.78,
          margin: { top: 0, right: 0, bottom: 1, left: 0 },
        };
        const pdfOpts = {
          paperSize: ["210mm", "400mm"],
          landscape: true,
          margin: { top: 0, right: 0, bottom: 1, left: 0 },
          multiPage: true,
        };
        const group = await drawing.drawDOM(rootEl, drawOpts);
        const dataURI = await drawing.exportPDF(group, pdfOpts);
        if (cancelled) return;
        // Expose the same totals the PDF renders so the WhatsApp body
        // (payable + unpaid count) is bit-identical to the printed PDF.
        // netDueAfterWallets is the same variable the PDF cell consumes for
        // "Net Due (After Shipping And Vehicle Wallet Balance)".
        window.__SOA_PDF_DATA_URI__ = dataURI;
        window.__SOA_PAYABLE__ = Number(netDueAfterWallets) || 0;
        window.__SOA_UNPAID_COUNT__ = Array.isArray(shippingSOA)
          ? shippingSOA.length
          : 0;
        window.__SOA_READY__ = true;
      } catch (e) {
        console.error("[print] kendo export failed:", e);
        window.__SOA_ERROR__ = (e && e.message) || String(e);
        window.__SOA_READY__ = true;
      }
    }, 2500); // give MUI/emotion + a large table enough time to paint
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [printMode, loader, shippingSOA, pdfVisibleColumns, printFetchError]);

  if (printMode) {
    if (!selectedClient || loader || !shippingSOA) {
      return (
        <Box
          id="soa-print-loading"
          sx={{ p: 4, fontFamily: "Arial, sans-serif" }}
        >
          Loading SOA…
        </Box>
      );
    }
    return (
      // Match the live page's wrapper structure exactly — no offscreen
      // positioning. Putting the wrapper at -99999px makes the Box
      // shrink-to-fit, which breaks inner flex layouts (e.g. inline
      // "Generated on:" + date) when kendo measures the tree. The inner
      // pdf-show display:none keeps the page visually blank.
      <Box>
        <PDFExport
          ref={printExportRef}
          margin={0}
          marginBottom={1}
          paperSize={["210mm", "400mm"]}
          fileName={getSafeSoaPdfFileName(selectedClient?.name)}
          dpi={96}
          landscape={true}
          scale={0.78}
        >
          <Box className="pdf-show" sx={{ display: "none" }}>
            <ShippingSoaPdf
              currencyExchangeRate={currencyExchangeRate}
              selectedCustomer={selectedClient}
              tableHead={tableHead}
              mode="landscape"
              fromdate={fromDate}
              todate={toDate}
              resultData={shippingSOA}
              total={totalBalance}
              selectedStatus={selectedStatus}
              shippingVault={shippingVault}
              totalVCC={totalVCC}
              vehicleVault={vehicleVault}
              mainTotal={mainTotal}
              totalBalance={totalBalance}
              totalPaid={totalPaid}
              allSums={allSums}
              netDueTotal={netDueTotal}
              shippingReceivableCV={shippingReceivableCV}
              totalShippingWallet={
                totalShippingWallet ? totalShippingWallet * -1 : 0
              }
              totalVehicleWallet={totalVehicleWallet}
              totalParkingWallet={
                totalParkingWallet && currencyExchangeRate
                  ? totalParkingWallet / currencyExchangeRate
                  : 0
              }
              visibleColumns={pdfVisibleColumns}
              dueBreakup={dueBreakup}
              subTotal={subTotal}
              copyContent={() => {}}
              copied={false}
              status={selectedStatus}
              totalCustomWallet={
                totalCustomWallet && currencyExchangeRate
                  ? totalCustomWallet / currencyExchangeRate
                  : 0
              }
            />
          </Box>
        </PDFExport>
      </Box>
    );
  }

  return (
    <Fragment>
      <Grid
        container
        spacing={1}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Grid item xs={12} sm={12} md={11}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mr: 4,
              my: 4,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: Colors.charcoalGrey,
                fontFamily: FontFamily.NunitoRegular,
                ml: "5px",
              }}
            >
              SHIPPING SOA V2
            </Typography>
            {shippingSOA?.length > 0 && (
              <Box
                sx={{
                  textAlign: "right",
                  p: 4,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Box>
                  <PDFExport
                    // paperSize="A4"
                    margin={0}
                    marginBottom={1}
                    ref={contentRef}
                    // scale={1}
                    paperSize={["210mm", "400mm"]}
                    fileName={getSafeSoaPdfFileName(selectedClient?.name)}
                    dpi={96}
                    landscape={true}
                    scale={0.78}
                  >
                    <Box className="pdf-show" sx={{ display: "none" }}>
                      <ShippingSoaPdf
                        currencyExchangeRate={currencyExchangeRate}
                        selectedCustomer={selectedClient}
                        tableHead={tableHead}
                        mode="landscape"
                        fromdate={fromDate}
                        todate={toDate}
                        resultData={shippingSOA}
                        total={totalBalance}
                        selectedStatus={selectedStatus}
                        shippingVault={shippingVault}
                        totalVCC={totalVCC}
                        vehicleVault={vehicleVault}
                        mainTotal={mainTotal}
                        totalBalance={totalBalance}
                        totalPaid={totalPaid}
                        allSums={allSums}
                        netDueTotal={netDueTotal}
                        shippingReceivableCV={shippingReceivableCV}
                        totalShippingWallet={
                          totalShippingWallet ? totalShippingWallet * -1 : 0
                        }
                        totalVehicleWallet={totalVehicleWallet}
                        totalParkingWallet={
                          totalParkingWallet && currencyExchangeRate
                            ? totalParkingWallet / currencyExchangeRate
                            : 0
                        }
                        visibleColumns={pdfVisibleColumns}
                        //visibleColumns={visibleColumns}
                        dueBreakup={dueBreakup}
                        subTotal={subTotal}
                        copyContent={copyContent}
                        copied={copied}
                        status={selectedStatus}
                        totalCustomWallet={
                          totalCustomWallet && currencyExchangeRate
                            ? totalCustomWallet / currencyExchangeRate
                            : 0
                        }
                      />
                    </Box>
                  </PDFExport>
                </Box>
                <PrimaryButton
                  title="Download PDF"
                  type="button"
                  style={{ backgroundColor: Colors.bluishCyan }}
                  onClick={handlePdfExport}
                  loading={isGeneratingPdf}
                  // Disabled if:
                  //   - a fetch is in flight (data still loading),
                  //   - another export is already running,
                  //   - there's no data on screen, or
                  //   - the on-screen customer doesn't match the dropdown
                  //     selection (race between switch + click).
                  disabled={
                    loader ||
                    isGeneratingPdf ||
                    isDownloadingExcel ||
                    !shippingSOA ||
                    shippingSOA.length === 0 ||
                    !selectedClient?.id ||
                    (loadedCustomerId != null &&
                      loadedCustomerId !== selectedClient.id)
                  }
                  // onClick={() => handleExportWithComponent(contentRef)}
                />
                {/* <PrimaryButton
                  title={"Download Excel"}
                  onClick={() => downloadExcel()}
                /> */}

<PrimaryButton
  title="Download Excel"
  type="button"
  style={{ backgroundColor: Colors.primary }}
  onClick={downloadExcel}
  loading={isDownloadingExcel}
  // Same safety gate as Download PDF above.
  disabled={
    loader ||
    isGeneratingPdf ||
    isDownloadingExcel ||
    !shippingSOA ||
    shippingSOA.length === 0 ||
    !selectedClient?.id ||
    (loadedCustomerId != null && loadedCustomerId !== selectedClient.id)
  }
/>
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item md={11}>
          <Box
            sx={{
              m: "10px 0 10px 0",
              // p: "20px",
              // bgcolor: Colors.feta,
              // border: `1px solid ${Colors.iron}`,
              // borderRadius: "9px",
            }}
          >
            <Grid
              container
              spacing={1}
              alignItems={"center"}
              component={"form"}
              onSubmit={handleSubmit(applyFilter)}
            >
              <Grid item xs={12} md={2}>
                <SelectField
                  size="small"
                  label={"Select Customer"}
                  options={clients}
                  selected={selectedClient}
                  onSelect={(value) => {
                    setSelectedClient(value);
                  }}
                  error={errors?.client?.message}
                  register={register("client", {
                    required: "Please select client.",
                  })}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <SelectField
                  size="small"
                  label={"Shipping Status"}
                  options={[
                    { id: "all", name: "All" },
                    { id: "true", name: "Paid" },
                    { id: "false", name: "Unpaid" },
                    { id: "arrivedanddue", name: "Arrived And Due (Port)" },
                    { id: "ontheway", name: "On The Way" },
                    { id: "arrivedgalaxyyard", name: "Arrived in Galaxy Yard" },
                  ]}
                  selected={selectedStatus}
                  onSelect={(value) => setSelectedStatus(value)}
                  error={errors?.status?.message}
                  register={register("status")}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <SelectField
                  size="small"
                  label={"Destination"}
                  options={destinations}
                  selected={selectedDestination}
                  onSelect={(value) => setSelectedDestination(value)}
                  onSearch={(v) => getDestinations(v)}
                  register={register("destination")}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={2}
                className="MuiStack-root"
                sx={{
                  paddingTop: "0px !important",
                }}
              >
                <Controller
                  control={control}
                  name="fromDate"
                  render={({ field }) => (
                    <DatePicker
                      label="From Arrived Port Date"
                      size="small"
                      maxDate={new Date()}
                      minDate={false}
                      value={fromDate}
                      placeholderText="Select date"
                      onChange={(date) => {
                        field.onChange(date);
                        handleFromDate(date);
                      }}
                      selected={field.value}
                    />
                  )}
                />
                {errors.fromDate && (
                  <Typography
                    color="error"
                    sx={{ fontSize: 12, textAlign: "left" }}
                  >
                    {errors.fromDate.message}
                  </Typography>
                )}
              </Grid>
              <Grid
                item
                xs={12}
                md={2}
                className="MuiStack-root"
                sx={{
                  paddingTop: "0px !important",
                }}
              >
                <Controller
                  control={control}
                  name="toDate"
                  render={({ field }) => (
                    <DatePicker
                      label="To Arrived Port Date"
                      size="small"
                      value={toDate}
                      minDate={fromDate}
                      maxDate={new Date()}
                      placeholderText="Select date"
                      onChange={(date) => {
                        field.onChange(date);
                        handleToDate(date);
                      }}
                      selected={field.value}
                    />
                  )}
                />
                {errors.toDate && (
                  <Typography
                    color="error"
                    sx={{ fontSize: 12, textAlign: "left" }}
                  >
                    {errors.toDate.message}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={2} sx={{ marginTop: "16px" }}>
                <Box>
                  <PrimaryButton
                    type="submit"
                    title="Search"
                    loading={loader}
                    // Search is also blocked while a PDF or Excel export is
                    // running so we can't start fetching a new customer's
                    // data underneath an in-flight download.
                    disabled={loader || isGeneratingPdf || isDownloadingExcel}
                  />
                </Box>
              </Grid>
              <Grid item md={7}></Grid>
              <Grid item md={3}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  {shippingSOA && (
                    <FormControl>
                      <InputLabel>Columns</InputLabel>
                      <Select
                        size={"small"}
                        multiple
                        value={visibleColumns}
                        label={"Columns"}
                        onChange={handleColumnChange}
                        renderValue={() => "Hide/Show"}
                        sx={{
                          borderRadius: "12px !important",
                          minWidth: "160px",
                        }}
                      >
                        {tableHead.map((column, index) => {
                          if (
                            column !== "Balance" &&
                            column !== "Past Due Days" &&
                            column !== "Paid"
                          ) {
                            return (
                              <MenuItem key={index} value={index}>
                                <Checkbox
                                  checked={visibleColumns.includes(index)}
                                />
                                <ListItemText primary={column} />
                              </MenuItem>
                            );
                          } else {
                            return null;
                          }
                        })}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid item md={11}>
          {shippingSOA && (
            <Box>
              {/*<Grid container mb={2}>
                 <Grid item xs={5}>
                  <FormControl>
                    <InputLabel>Columns</InputLabel>
                    <Select
                      size={"small"}
                      multiple
                      value={visibleColumns}
                      label={"Columns"}
                      onChange={handleColumnChange}
                      renderValue={() => "Show/Hide"}
                      sx={{ borderRadius: "12px !important" }}
                    >
                      {tableHead.map((column, index) => {
                        if (
                          column !== "Balance" &&
                          column !== "Past Due Days" &&
                          column !== "Paid"
                        ) {
                          return (
                            <MenuItem key={index} value={index}>
                              <Checkbox
                                checked={visibleColumns.includes(index)}
                              />
                              <ListItemText primary={column} />
                            </MenuItem>
                          );
                        } else {
                          return null;
                        }
                      })}
                    </Select>
                  </FormControl>
                </Grid> 
              </Grid>*/}

              {shippingSOA && (
                <Box>
                  <TableContainer
                    component={Paper}
                    sx={{
                      boxShadow: "0px 8px 18px 0px #9B9B9B1A",
                      borderRadius: 2,
                      maxHeight: "calc(100vh - 330px)",
                    }}
                    className="table-box"
                  >
                    <Table stickyHeader sx={{ minWidth: 500 }}>
                      {/* Table Header */}
                      <TableHead>
                        <TableRow>
                          {visibleColumns.map((index) => (
                            <Cell className="pdf-table" key={index}>
                              {tableHead[index] === "War Sur.Char Disc" ? (
                                <span style={{ color: Colors.danger }}>
                                  {tableHead[index]}
                                </span>
                              ) : (
                                tableHead[index]
                              )}{" "}
                              {["Buy Date", "Arrived Date"].includes(
                                tableHead[index],
                              ) ? (
                                <>
                                  <span className="pdf-hide">
                                    <ArrowUpwardIcon
                                      sx={{
                                        color: "white",
                                        fontSize: "15px",
                                        cursor: "pointer",
                                      }}
                                      onClick={(e) =>
                                        sortData(
                                          e,
                                          "ascending",
                                          tableHead[index],
                                        )
                                      }
                                    />
                                  </span>
                                  <span className="pdf-hide">
                                    <ArrowDownwardIcon
                                      sx={{
                                        color: "white",
                                        fontSize: "15px",
                                        cursor: "pointer",
                                      }}
                                      onClick={(e) =>
                                        sortData(
                                          e,
                                          "descending",
                                          tableHead[index],
                                        )
                                      }
                                    />
                                  </span>
                                </>
                              ) : (
                                ""
                              )}
                            </Cell>
                          ))}
                        </TableRow>
                      </TableHead>

                      {/* Table Body */}
                      <TableBody>
                        {!loader ? (
                          shippingSOA?.length > 0 ? (
                            <Fragment>
                              {shippingSOA?.map((item, rowIndex) => {
                                const isActive = true;
                                return (
                                  <Row
                                    key={rowIndex}
                                    sx={{
                                      bgcolor: rowIndex % 2 !== 0 && "#EFF8E7",
                                    }}
                                  >
                                    {visibleColumns.map((colIndex) => (
                                      <Cell
                                        className="pdf-table"
                                        key={colIndex}
                                      >
                                        {renderCellContent(
                                          rowIndex,
                                          colIndex,
                                          item,
                                          isActive,
                                        )}
                                      </Cell>
                                    ))}
                                  </Row>
                                );
                              })}
                              <Row>
                                {visibleColumns.map((colIndex, visibleIndex) => {
                                  const totalsByHeader = {
                                    "Sub Total": subTotal,
                                    "Net Due": mainTotal,
                                    Paid: totalPaid,
                                    "Balance Due": totalBalance,
                                  };
                                  const value = totalsByHeader[tableHead[colIndex]];

                                  return (
                                    <Cell key={`totals-${colIndex}`}>
                                      {visibleIndex === 0 ? (
                                        <Typography
                                          className="pdf-table"
                                          variant="body1"
                                          sx={{ fontWeight: 700 }}
                                        >
                                          Total
                                        </Typography>
                                      ) : value !== undefined ? (
                                        <Typography
                                          className="pdf-table"
                                          variant="body1"
                                          sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                                        >
                                          $ {CommaSeparator(parseFloat(value || 0).toFixed(2))}
                                        </Typography>
                                      ) : null}
                                    </Cell>
                                  );
                                })}
                              </Row>
                              <Row>
                                <Cell colSpan={32}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    Total Parking Due (for Breakdown Download
                                    Gate Pass Report)
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>

                                <Cell colSpan={3}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    $
                                    {formatAccounting(
                                      parseFloat(
                                        totalParkingWallet &&
                                          currencyExchangeRate
                                          ? totalParkingWallet /
                                              currencyExchangeRate
                                          : 0,
                                      ).toFixed(2),
                                    )}
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>
                                <Cell></Cell>
                              </Row>
                              <Row>
                                <Cell colSpan={31}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    Shipping Wallet Balance
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>
                                <Cell></Cell>

                                <Cell colSpan={3}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    $
                                    {formatAccounting(
                                      parseFloat(
                                        (totalShippingWallet
                                          ? totalShippingWallet
                                          : 0) * -1,
                                      ).toFixed(2),
                                    )}
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>
                                <Cell></Cell>
                              </Row>{" "}
                              <Row>
                                <Cell colSpan={32}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    Vehicle Wallet Balance
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>

                                <Cell colSpan={3}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    $
                                    {formatAccounting(
                                      parseFloat(
                                        (totalVehicleWallet &&
                                        currencyExchangeRate
                                          ? totalVehicleWallet /
                                            currencyExchangeRate
                                          : 0) * -1,
                                      ).toFixed(2),
                                    )}
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>
                              </Row>
                              <Row>
                                <Cell colSpan={32}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    Net Due(After Shipping And Vehicle Wallet
                                    Balance)
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>

                                <Cell colSpan={3}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    $ {formatAccounting(netDueAfterWallets)}
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>
                              </Row>
                              <Row>
                                <Cell colSpan={32}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    undue Custom (Not Charged Yet)
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>

                                <Cell colSpan={3}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    $
                                    {formatAccounting(
                                      parseFloat(
                                        totalCustomWallet &&
                                          currencyExchangeRate
                                          ? totalCustomWallet /
                                              currencyExchangeRate
                                          : 0,
                                      ).toFixed(2),
                                    )}
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>
                              </Row>
                              <Row>
                                <Cell colSpan={32}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    Shipping Recieveable with undue Custom
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>

                                <Cell colSpan={3}>
                                  <Typography
                                    className="pdf-table"
                                    variant="body1"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    $
                                    {formatAccounting(
                                      parseFloat(
                                        parseFloat(netDueTotal) +
                                          parseFloat(
                                            (totalVehicleWallet &&
                                            currencyExchangeRate
                                              ? totalVehicleWallet /
                                                currencyExchangeRate
                                              : 0) * -1,
                                          ) +
                                          parseFloat(
                                            totalCustomWallet &&
                                              currencyExchangeRate
                                              ? totalCustomWallet /
                                                  currencyExchangeRate
                                              : 0,
                                          ),
                                      ).toFixed(2),
                                    )}
                                  </Typography>
                                </Cell>
                                <Cell></Cell>
                                <Cell></Cell>
                              </Row>
                            </Fragment>
                          ) : (
                            <Row>
                              <Cell
                                colSpan={tableHead.length + 1}
                                align="center"
                                sx={{ fontWeight: 600 }}
                              >
                                No Data Found
                              </Cell>
                            </Row>
                          )
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={visibleColumns?.length + 2}
                              align="center"
                              sx={{ fontWeight: 600 }}
                            >
                              <Box className={classes.loaderWrap}>
                                <CircularProgress />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ py: 1, bgcolor: Colors.whiteSmoke }}>
                    <Grid container spacing={1}>
                      <Grid
                        item
                        xs={3}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Typography
                          sx={{
                            fontSize: "12px !important",
                            fontWeight: 700,
                            fontFamily: FontFamily.openSans,
                            marginRight: "2px",
                          }}
                        >
                          Ageing
                        </Typography>
                        <Box
                          sx={{
                            border: "1px solid #B2B5BA",
                            background: "white",
                            fontFamily: FontFamily.openSans,
                            padding: "4px",
                            textAlign: "center",
                            color: "black",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "75px", // full width inside grid cell
                            height: "40px",
                            fontSize: "8px !important",
                            fontWeight: 700,
                            gap: "10px",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "9px !important",
                                fontWeight: 700,
                                fontFamily: FontFamily.openSans,
                              }}
                            >
                              Below 15 days
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "9px !important",
                                fontWeight: 700,
                                fontFamily: FontFamily.openSans,
                              }}
                            >
                              $
                              {CommaSeparator(
                                dueBreakup?.below15DaysTotal,
                              )}{" "}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={3}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            border: "1px solid #B2B5BA",
                            background: "white",
                            fontFamily: FontFamily.openSans,
                            padding: "4px",
                            textAlign: "center",
                            color: "black",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "75px",
                            height: "40px",
                            fontSize: "9px !important",
                            fontWeight: 700,
                            gap: "10px",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "9px !important",
                                fontWeight: 700,
                                fontFamily: FontFamily.openSans,
                              }}
                            >
                              16 - 30 Days
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px !important",
                                fontWeight: 700,
                                fontFamily: FontFamily.openSans,
                              }}
                            >
                              $
                              {CommaSeparator(
                                dueBreakup?.below16To30DaysTotal,
                              )}{" "}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={3}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            border: "1px solid #B2B5BA",
                            background: "white",
                            fontFamily: FontFamily.openSans,
                            padding: "4px",
                            textAlign: "center",
                            color: "black",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "75px",
                            height: "40px",
                            fontSize: "9px !important",
                            fontWeight: 700,
                            gap: "10px",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "9px !important",
                                fontWeight: 700,
                                fontFamily: FontFamily.openSans,
                              }}
                            >
                              Over 30 Days
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px !important",
                                fontWeight: 700,
                                fontFamily: FontFamily.openSans,
                              }}
                            >
                              $
                              {CommaSeparator(dueBreakup?.over30DaysTotal)}{" "}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={3}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            border: "1px solid #B2B5BA",
                            background: "white",
                            fontFamily: FontFamily.openSans,
                            padding: "4px",
                            textAlign: "center",
                            color: "black",
                            borderRadius: "6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "75px",
                            height: "40px",
                            fontSize: "9px !important",
                            fontWeight: 700,
                            gap: "10px",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "9px !important",
                                fontWeight: 700,
                                fontFamily: FontFamily.openSans,
                              }}
                            >
                              Total
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px !important",
                                fontWeight: 700,
                                fontFamily: FontFamily.openSans,
                              }}
                            >
                              ${CommaSeparator(dueBreakup?.total)}{" "}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* <Box sx={{ py: 1, bgcolor: Colors.whiteSmoke }}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={3}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            className="pdf-table"
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              fontFamily: FontFamily.NunitoRegular,
                              color: "black", // Set text color to black
                            }}
                          >
                            Receiveable Ageing
                          </Typography>
                          <Box
                            sx={{
                              textAlign: "center",
                              p: 1,
                              width: "130px",
                              bgcolor: Colors.flashWhite,
                              border: "1px solid #B2B5BA", // Set border color to black
                              borderRadius: "4px",
                            }}
                          >
                            <Typography
                              className="pdf-table"
                              variant="body2"
                              sx={{ color: "black" }} // Set text color to black
                            >
                              {CommaSeparator(totalVCC)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              textAlign: "center",
                              p: 1,
                              width: "130px",
                              bgcolor: Colors.flashWhite,
                              border: "1px solid #B2B5BA", // Set border color to black
                              borderRadius: "4px",
                            }}
                          >
                            <Typography
                              className="pdf-table"
                              variant="body2"
                              sx={{ color: "black" }} // Set text color to black
                            >
                              ${CommaSeparator(parseFloat(totalShippingWallet).toFixed(2))}

                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >

                          <Box
                            sx={{
                              textAlign: "center",
                              p: 1,
                              width: "130px",
                              bgcolor: Colors.flashWhite,
                              border: "1px solid #B2B5BA", // Set border color to black
                              borderRadius: "4px",
                            }}
                          >
                            <Typography
                              className="pdf-table"
                              variant="body2"
                              sx={{ color: "black" }} // Set text color to black
                            >
                              ${CommaSeparator(parseFloat(totalVehicleWallet / currencyExchangeRate).toFixed(2))}

                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >

                          <Box
                            sx={{
                              textAlign: "center",
                              p: 1,
                              width: "130px",
                              bgcolor: Colors.flashWhite,
                              border: "1px solid #B2B5BA", // Set border color to black
                              borderRadius: "4px",
                            }}
                          >
                            <Typography
                              className="pdf-table"
                              variant="body2"
                              sx={{ color: "black" }} // Set text color to black
                            >
                              $ {CommaSeparator(parseFloat(parseFloat(totalBalance) - Math.abs(
                                totalShippingWallet
                              )).toFixed(2))}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box> */}
                </Box>
              )}

              {loader && <CircleLoading />}
            </Box>
          )}
        </Grid>
      </Grid>
    </Fragment>
  );
}

export default ShippingSOAV2;
